/*
 * Notifications the server writes in English, exactly as it writes them: one entry per
 * type, one variant per wording (fallbacks such as "A colleague" or "an asset" are their
 * own variants and come BEFORE the general form, which would also read them). The app
 * and send-push read a stored row back through these to get its names and dates, then
 * show the reader's language (../localizeNotification.ts). Translations live beside this
 * file (<lang>.ts) with the same keys; locales/<lang>/notifications.ts re-exports them.
 *
 * When a server message's English changes, change it here too: until then that row
 * reads exactly as stored. Keep the old wording as "<id>Legacy" (before any form that
 * would read it) so rows already stored still translate; its translation may be the
 * corrected one. notificationLocale.current.test.ts checks every template
 * reads back its own values and that no variant is hidden behind a more general one.
 *
 * lines: the sentences inside the stalled-work digest ({{line}}). values: fixed English
 * words the server puts in a slot (a reason, a role), translated when shown.
 */
export const notifications = {
  "types": {
    "feature_request_update": {
      "status": {
        "title": "Your feature request is {{featureStatus}}",
        "body": "\"{{request}}\" is {{featureStatus}}."
      },
      "reply": {
        "title": "Reply to your feature request",
        "body": "\"{{request}}\": {{reply}}"
      },
      "merged": {
        "title": "Already requested",
        "body": "\"{{request}}\" was already requested. We've added your vote."
      }
    },
    "feature_idea_update": {
      "default": {
        "title": "An idea you voted for is {{featureStatus}}",
        "body": "\"{{idea}}\" is {{featureStatus}}."
      }
    },
    "work_change_opened": {
      "default": {
        "title": "The work is being changed",
        "body": "{{actor}} is changing \"{{job}}\". You will be asked to accept it again."
      }
    },
    "work_changed": {
      "default": {
        "title": "The work changed. Accept it again",
        "body": "{{actor}} changed \"{{job}}\": {{changes}}. Review it and accept again."
      }
    },
    "quote_scope_changed": {
      "default": {
        "title": "The scope you were quoting changed",
        "body": "{{actor}} changed \"{{job}}\": {{changes}}. Quote again on the new scope."
      }
    },
    "work_accepted_longer": {
      "default": {
        "title": "Accepted, and it now takes longer",
        "body": "{{actor}} accepted \"{{job}}\" and now needs {{minutes}} minutes. Check the due date."
      }
    },
    "quote_accepted": {
      "noJob": {
        "title": "Your quote was accepted",
        "body": "Your quote for a job was accepted. The work is yours."
      },
      "default": {
        "title": "Your quote was accepted",
        "body": "Your quote for \"{{job}}\" was accepted. The work is yours."
      }
    },
    "asset_request_job_assigned": {
      "noActor": {
        "title": "Work assigned",
        "body": "The team assigned the job for {{request}} to {{assignee}}."
      },
      "default": {
        "title": "Work assigned",
        "body": "{{actor}} assigned the job for {{request}} to {{assignee}}."
      }
    },
    "asset_request_job_accepted": {
      "noActor": {
        "title": "Job accepted",
        "body": "The team accepted the job for {{request}}."
      },
      "default": {
        "title": "Job accepted",
        "body": "{{actor}} accepted the job for {{request}}."
      }
    },
    "asset_request_job_declined": {
      "noActor": {
        "title": "Job declined",
        "body": "The team can't take the job for {{request}}. The team will find someone else."
      },
      "default": {
        "title": "Job declined",
        "body": "{{actor}} can't take the job for {{request}}. The team will find someone else."
      }
    },
    "asset_request_job_verified": {
      "noActor": {
        "title": "Work verified",
        "body": "The team verified the completed work for {{request}}."
      },
      "default": {
        "title": "Work verified",
        "body": "{{actor}} verified the completed work for {{request}}."
      }
    },
    "asset_request_inspection_approved": {
      "noActor": {
        "title": "Inspection signed off",
        "body": "The team signed off the inspection for {{request}}."
      },
      "default": {
        "title": "Inspection signed off",
        "body": "{{actor}} signed off the inspection for {{request}}."
      }
    },
    "asset_request_submitted": {
      "noActor": {
        "title": "A request was raised for you",
        "body": "The team raised \"{{request}}\" for you on {{asset}}."
      },
      "default": {
        "title": "A request was raised for you",
        "body": "{{actor}} raised \"{{request}}\" for you on {{asset}}."
      }
    },
    "job_started": {
      "noActor": {
        "title": "Job started",
        "body": "The assignee started \"{{job}}\"."
      },
      "default": {
        "title": "Job started",
        "body": "{{actor}} started \"{{job}}\"."
      }
    },
    "job_paused": {
      "noActorReason": {
        "title": "Job paused",
        "body": "The assignee paused \"{{job}}\". {{reason}}"
      },
      "noActor": {
        "title": "Job paused",
        "body": "The assignee paused \"{{job}}\"."
      },
      "reason": {
        "title": "Job paused",
        "body": "{{actor}} paused \"{{job}}\". {{reason}}"
      },
      "default": {
        "title": "Job paused",
        "body": "{{actor}} paused \"{{job}}\"."
      }
    },
    "job_resumed": {
      "noActor": {
        "title": "Job resumed",
        "body": "The assignee resumed \"{{job}}\"."
      },
      "default": {
        "title": "Job resumed",
        "body": "{{actor}} resumed \"{{job}}\"."
      }
    },
    "job_evidence_added": {
      "noActor": {
        "title": "Evidence added",
        "body": "The assignee added the evidence you asked for on \"{{job}}\". Review it to close the job."
      },
      "default": {
        "title": "Evidence added",
        "body": "{{actor}} added the evidence you asked for on \"{{job}}\". Review it to close the job."
      }
    },
    "inspection_cancelled": {
      "noActorNoAssetReason": {
        "title": "Inspection cancelled",
        "body": "A colleague cancelled the inspection on an asset. {{reason}}"
      },
      "noActorNoAsset": {
        "title": "Inspection cancelled",
        "body": "A colleague cancelled the inspection on an asset."
      },
      "noActorReason": {
        "title": "Inspection cancelled",
        "body": "A colleague cancelled the inspection on {{asset}}. {{reason}}"
      },
      "noActor": {
        "title": "Inspection cancelled",
        "body": "A colleague cancelled the inspection on {{asset}}."
      },
      "reason": {
        "title": "Inspection cancelled",
        "body": "{{actor}} cancelled the inspection on {{asset}}. {{reason}}"
      },
      "default": {
        "title": "Inspection cancelled",
        "body": "{{actor}} cancelled the inspection on {{asset}}."
      }
    },
    "asset_collaboration_shared": {
      "noAsset": {
        "title": "An asset was shared with you",
        "body": "Asset — open to review and accept access."
      },
      "default": {
        "title": "An asset was shared with you",
        "body": "{{asset}} — open to review and accept access."
      }
    },
    "asset_in_service": {
      "backNoAsset": {
        "title": "Asset back in service",
        "body": "An asset is in service. Schedules resume from today. {{note}}"
      },
      "backNoAssetNoNote": {
        "title": "Asset back in service",
        "body": "An asset is in service. Schedules resume from today."
      },
      "firstNoAsset": {
        "title": "Asset in service",
        "body": "An asset is in service. Schedules resume from today. {{note}}"
      },
      "firstNoAssetNoNote": {
        "title": "Asset in service",
        "body": "An asset is in service. Schedules resume from today."
      },
      "back": {
        "title": "Asset back in service",
        "body": "{{asset}} is in service. Schedules resume from today. {{note}}"
      },
      "backNoNote": {
        "title": "Asset back in service",
        "body": "{{asset}} is in service. Schedules resume from today."
      },
      "first": {
        "title": "Asset in service",
        "body": "{{asset}} is in service. Schedules resume from today. {{note}}"
      },
      "firstNoNote": {
        "title": "Asset in service",
        "body": "{{asset}} is in service. Schedules resume from today."
      }
    },
    "asset_out_of_service": {
      "noAsset": {
        "title": "Asset out of service",
        "body": "An asset is out of service ({{reason}}). Its schedules are paused until it is recommissioned. {{note}}"
      },
      "noAssetNoNote": {
        "title": "Asset out of service",
        "body": "An asset is out of service ({{reason}}). Its schedules are paused until it is recommissioned."
      },
      "noAssetNoReason": {
        "title": "Asset out of service",
        "body": "An asset is out of service. Its schedules are paused until it is recommissioned. {{note}}"
      },
      "noAssetNoReasonNoNote": {
        "title": "Asset out of service",
        "body": "An asset is out of service. Its schedules are paused until it is recommissioned."
      },
      "default": {
        "title": "Asset out of service",
        "body": "{{asset}} is out of service ({{reason}}). Its schedules are paused until it is recommissioned. {{note}}"
      },
      "noNote": {
        "title": "Asset out of service",
        "body": "{{asset}} is out of service ({{reason}}). Its schedules are paused until it is recommissioned."
      },
      "noReason": {
        "title": "Asset out of service",
        "body": "{{asset}} is out of service. Its schedules are paused until it is recommissioned. {{note}}"
      },
      "noReasonNoNote": {
        "title": "Asset out of service",
        "body": "{{asset}} is out of service. Its schedules are paused until it is recommissioned."
      }
    },
    "asset_owner_assigned": {
      "default": {
        "title": "New asset responsibility",
        "body": "{{asset}} is now your responsibility."
      }
    },
    "asset_owner_removed": {
      "default": {
        "title": "Asset responsibility changed",
        "body": "{{asset}} is no longer your responsibility."
      }
    },
    "asset_request_approved": {
      "noAsset": {
        "title": "Your request was approved",
        "body": "\"{{request}}\" was approved for the asset."
      },
      "default": {
        "title": "Your request was approved",
        "body": "\"{{request}}\" was approved for {{asset}}."
      },
      "withNote": {
        "title": "Your request was approved",
        "body": "\"{{request}}\" was approved: {{note}}"
      }
    },
    "asset_request_awaiting_customer": {
      "noAsset": {
        "title": "Action needed on your request",
        "body": "{{request}} is waiting on a response from you for the asset."
      },
      "default": {
        "title": "Action needed on your request",
        "body": "{{request}} is waiting on a response from you for {{asset}}."
      },
      "note": {
        "title": "Action needed on your request",
        "body": "{{note}}"
      }
    },
    "asset_request_closed": {
      "byActor": {
        "title": "Case closed — all work complete",
        "body": "{{actor}} verified the last of the work on {{request}}. Everything was completed and accepted."
      },
      "default": {
        "title": "Case closed — all work complete",
        "body": "{{request}}: everything was completed and accepted. Nothing more to do."
      },
      "operatorClosed": {
        "title": "Your request was closed",
        "body": "{{request}} was marked done."
      }
    },
    "asset_request_commented": {
      "requesterNoRequestPhotoLegacy": {
        "title": "New message",
        "body": "The requester on \"a request\": shared a photo."
      },
      "requesterNoRequestPhoto": {
        "title": "New message",
        "body": "The requester on a request: shared a photo."
      },
      "teamNoRequestPhotoLegacy": {
        "title": "New message",
        "body": "The team on \"a request\": shared a photo."
      },
      "teamNoRequestPhoto": {
        "title": "New message",
        "body": "The team on a request: shared a photo."
      },
      "requesterPhoto": {
        "title": "New message",
        "body": "The requester on \"{{request}}\": shared a photo."
      },
      "noRequestPhotoLegacy": {
        "title": "New message",
        "body": "{{author}} on \"a request\": shared a photo."
      },
      "noRequestPhoto": {
        "title": "New message",
        "body": "{{author}} on a request: shared a photo."
      },
      "teamPhoto": {
        "title": "New message",
        "body": "The team on \"{{request}}\": shared a photo."
      },
      "requesterNoRequestLegacy": {
        "title": "New message",
        "body": "The requester on \"a request\": {{message}}"
      },
      "requesterNoRequest": {
        "title": "New message",
        "body": "The requester on a request: {{message}}"
      },
      "teamNoRequestLegacy": {
        "title": "New message",
        "body": "The team on \"a request\": {{message}}"
      },
      "teamNoRequest": {
        "title": "New message",
        "body": "The team on a request: {{message}}"
      },
      "photo": {
        "title": "New message",
        "body": "{{author}} on \"{{request}}\": shared a photo."
      },
      "requester": {
        "title": "New message",
        "body": "The requester on \"{{request}}\": {{message}}"
      },
      "noRequestLegacy": {
        "title": "New message",
        "body": "{{author}} on \"a request\": {{message}}"
      },
      "noRequest": {
        "title": "New message",
        "body": "{{author}} on a request: {{message}}"
      },
      "team": {
        "title": "New message",
        "body": "The team on \"{{request}}\": {{message}}"
      },
      "default": {
        "title": "New message",
        "body": "{{author}} on \"{{request}}\": {{message}}"
      }
    },
    "asset_request_completion_accepted": {
      "verifierNoRequesterNoRequestLegacy": {
        "title": "Requester is happy — verify to close",
        "body": "The requester accepted the completed work on \"the request\". Verify the job to close it."
      },
      "verifierNoRequesterNoRequest": {
        "title": "Requester is happy — verify to close",
        "body": "The requester accepted the completed work on the request. Verify the job to close it."
      },
      "verifierNoRequester": {
        "title": "Requester is happy — verify to close",
        "body": "The requester accepted the completed work on \"{{request}}\". Verify the job to close it."
      },
      "verifierNoRequestLegacy": {
        "title": "Requester is happy — verify to close",
        "body": "{{requester}} accepted the completed work on \"the request\". Verify the job to close it."
      },
      "verifierNoRequest": {
        "title": "Requester is happy — verify to close",
        "body": "{{requester}} accepted the completed work on the request. Verify the job to close it."
      },
      "verifier": {
        "title": "Requester is happy — verify to close",
        "body": "{{requester}} accepted the completed work on \"{{request}}\". Verify the job to close it."
      },
      "informedNoRequesterNoRequestLegacy": {
        "title": "Requester accepted the work",
        "body": "The requester accepted the completed work on \"the request\"."
      },
      "informedNoRequesterNoRequest": {
        "title": "Requester accepted the work",
        "body": "The requester accepted the completed work on the request."
      },
      "informedNoRequester": {
        "title": "Requester accepted the work",
        "body": "The requester accepted the completed work on \"{{request}}\"."
      },
      "informedNoRequestLegacy": {
        "title": "Requester accepted the work",
        "body": "{{requester}} accepted the completed work on \"the request\"."
      },
      "informedNoRequest": {
        "title": "Requester accepted the work",
        "body": "{{requester}} accepted the completed work on the request."
      },
      "informed": {
        "title": "Requester accepted the work",
        "body": "{{requester}} accepted the completed work on \"{{request}}\"."
      }
    },
    "asset_request_completion_rejected": {
      "noActor": {
        "title": "Completion rejected",
        "body": "The team rejected the completed work for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Completion rejected",
        "body": "The team rejected the completed work for {{request}}."
      },
      "default": {
        "title": "Completion rejected",
        "body": "{{actor}} rejected the completed work for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Completion rejected",
        "body": "{{actor}} rejected the completed work for {{request}}."
      }
    },
    "asset_request_confirmed_schedule": {
      "requestNoRequest": {
        "title": "Date confirmed",
        "body": "Request — the requester confirmed the scheduled visit."
      },
      "visitNoInspection": {
        "title": "Visit confirmed",
        "body": "The visit for the inspection on {{when}} was confirmed."
      },
      "request": {
        "title": "Date confirmed",
        "body": "{{request}} — the requester confirmed the scheduled visit."
      },
      "visitNoJob": {
        "title": "Visit confirmed",
        "body": "The visit for the job on {{when}} was confirmed."
      },
      "visit": {
        "title": "Visit confirmed",
        "body": "The visit for {{work}} on {{when}} was confirmed."
      }
    },
    "asset_request_converted": {
      "toInspection": {
        "title": "Your request was converted into an inspection",
        "body": "{{request}} is now scheduled for operational work."
      },
      "toJob": {
        "title": "Your request was converted into a job",
        "body": "{{request}} is now in the work queue."
      },
      "inspectionUndated": {
        "title": "Inspection scheduled",
        "body": "{{request}} is now scheduled for an inspection."
      },
      "jobDue": {
        "title": "Your request is now a job",
        "body": "{{request}} is now a job for {{assignee}}, due by {{dueDate}}."
      },
      "inspectionScheduled": {
        "title": "Inspection scheduled",
        "body": "{{request}} — inspection scheduled for {{scheduledWhen}}."
      },
      "jobDueNoAssignee": {
        "title": "Your request is now a job",
        "body": "{{request}} is now a job, due by {{dueDate}}."
      },
      "job": {
        "title": "Your request is now a job",
        "body": "{{request}} is now a job for {{assignee}}."
      },
      "jobScheduled": {
        "title": "Job scheduled",
        "body": "{{request}} — job for {{assignee}} scheduled for {{scheduledDate}}."
      },
      "jobNoAssignee": {
        "title": "Your request is now a job",
        "body": "{{request}} is now a job."
      },
      "jobScheduledNoAssignee": {
        "title": "Job scheduled",
        "body": "{{request}} — job scheduled for {{scheduledDate}}."
      }
    },
    "asset_request_created": {
      "noAssetOperatorNoRequestPublicLinkLegacy": {
        "title": "New request for asset",
        "body": "An operator submitted \"a request\" via the public link."
      },
      "noAssetOperatorNoRequestPublicLink": {
        "title": "New request for asset",
        "body": "An operator submitted a request via the public link."
      },
      "noAssetContactNoRequestPublicLinkLegacy": {
        "title": "New request for asset",
        "body": "A contact submitted \"a request\" via the public link."
      },
      "noAssetContactNoRequestPublicLink": {
        "title": "New request for asset",
        "body": "A contact submitted a request via the public link."
      },
      "operatorNoRequestPublicLinkLegacy": {
        "title": "New request for {{asset}}",
        "body": "An operator submitted \"a request\" via the public link."
      },
      "operatorNoRequestPublicLink": {
        "title": "New request for {{asset}}",
        "body": "An operator submitted a request via the public link."
      },
      "contactNoRequestPublicLinkLegacy": {
        "title": "New request for {{asset}}",
        "body": "A contact submitted \"a request\" via the public link."
      },
      "contactNoRequestPublicLink": {
        "title": "New request for {{asset}}",
        "body": "A contact submitted a request via the public link."
      },
      "noAssetOperatorPublicLink": {
        "title": "New request for asset",
        "body": "An operator submitted \"{{request}}\" via the public link."
      },
      "noAssetNoRequestPublicLinkLegacy": {
        "title": "New request for asset",
        "body": "{{submitter}} submitted \"a request\" via the public link."
      },
      "noAssetNoRequestPublicLink": {
        "title": "New request for asset",
        "body": "{{submitter}} submitted a request via the public link."
      },
      "noAssetContactPublicLink": {
        "title": "New request for asset",
        "body": "A contact submitted \"{{request}}\" via the public link."
      },
      "operatorPublicLink": {
        "title": "New request for {{asset}}",
        "body": "An operator submitted \"{{request}}\" via the public link."
      },
      "noRequestPublicLinkLegacy": {
        "title": "New request for {{asset}}",
        "body": "{{submitter}} submitted \"a request\" via the public link."
      },
      "noRequestPublicLink": {
        "title": "New request for {{asset}}",
        "body": "{{submitter}} submitted a request via the public link."
      },
      "contactPublicLink": {
        "title": "New request for {{asset}}",
        "body": "A contact submitted \"{{request}}\" via the public link."
      },
      "noAssetPublicLink": {
        "title": "New request for asset",
        "body": "{{submitter}} submitted \"{{request}}\" via the public link."
      },
      "noAssetOperatorNoRequestLegacy": {
        "title": "New request for asset",
        "body": "An operator submitted \"a request\"."
      },
      "noAssetOperatorNoRequest": {
        "title": "New request for asset",
        "body": "An operator submitted a request."
      },
      "noAssetContactNoRequestLegacy": {
        "title": "New request for asset",
        "body": "A contact submitted \"a request\"."
      },
      "noAssetContactNoRequest": {
        "title": "New request for asset",
        "body": "A contact submitted a request."
      },
      "publicLink": {
        "title": "New request for {{asset}}",
        "body": "{{submitter}} submitted \"{{request}}\" via the public link."
      },
      "operatorNoRequestLegacy": {
        "title": "New request for {{asset}}",
        "body": "An operator submitted \"a request\"."
      },
      "operatorNoRequest": {
        "title": "New request for {{asset}}",
        "body": "An operator submitted a request."
      },
      "contactNoRequestLegacy": {
        "title": "New request for {{asset}}",
        "body": "A contact submitted \"a request\"."
      },
      "contactNoRequest": {
        "title": "New request for {{asset}}",
        "body": "A contact submitted a request."
      },
      "noAssetOperator": {
        "title": "New request for asset",
        "body": "An operator submitted \"{{request}}\"."
      },
      "noAssetNoRequestLegacy": {
        "title": "New request for asset",
        "body": "{{submitter}} submitted \"a request\"."
      },
      "noAssetNoRequest": {
        "title": "New request for asset",
        "body": "{{submitter}} submitted a request."
      },
      "noAssetContact": {
        "title": "New request for asset",
        "body": "A contact submitted \"{{request}}\"."
      },
      "operator": {
        "title": "New request for {{asset}}",
        "body": "An operator submitted \"{{request}}\"."
      },
      "noRequestLegacy": {
        "title": "New request for {{asset}}",
        "body": "{{submitter}} submitted \"a request\"."
      },
      "noRequest": {
        "title": "New request for {{asset}}",
        "body": "{{submitter}} submitted a request."
      },
      "contact": {
        "title": "New request for {{asset}}",
        "body": "A contact submitted \"{{request}}\"."
      },
      "noAsset": {
        "title": "New request for asset",
        "body": "{{submitter}} submitted \"{{request}}\"."
      },
      "default": {
        "title": "New request for {{asset}}",
        "body": "{{submitter}} submitted \"{{request}}\"."
      }
    },
    "asset_request_evidence_requested": {
      "noAsset": {
        "title": "Request updated",
        "body": "{{request}} was updated for the asset. {{note}}"
      },
      "noAssetNoNote": {
        "title": "Request updated",
        "body": "{{request}} was updated for the asset."
      },
      "default": {
        "title": "Request updated",
        "body": "{{request}} was updated for {{asset}}. {{note}}"
      },
      "noNote": {
        "title": "Request updated",
        "body": "{{request}} was updated for {{asset}}."
      }
    },
    "asset_request_inspection_cancelled": {
      "noActor": {
        "title": "Inspection cancelled",
        "body": "The team cancelled the inspection for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Inspection cancelled",
        "body": "The team cancelled the inspection for {{request}}."
      },
      "default": {
        "title": "Inspection cancelled",
        "body": "{{actor}} cancelled the inspection for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Inspection cancelled",
        "body": "{{actor}} cancelled the inspection for {{request}}."
      }
    },
    "asset_request_inspection_completed": {
      "noActor": {
        "title": "Inspection completed",
        "body": "The team completed the inspection for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Inspection completed",
        "body": "The team completed the inspection for {{request}}."
      },
      "default": {
        "title": "Inspection completed",
        "body": "{{actor}} completed the inspection for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Inspection completed",
        "body": "{{actor}} completed the inspection for {{request}}."
      }
    },
    "asset_request_inspection_paused": {
      "noActor": {
        "title": "Inspection paused",
        "body": "The team paused the inspection for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Inspection paused",
        "body": "The team paused the inspection for {{request}}."
      },
      "default": {
        "title": "Inspection paused",
        "body": "{{actor}} paused the inspection for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Inspection paused",
        "body": "{{actor}} paused the inspection for {{request}}."
      }
    },
    "asset_request_inspection_reassigned": {
      "toAssigneeNoActor": {
        "title": "Inspection reassigned",
        "body": "The team reassigned the inspection for {{request}} to {{assignee}}."
      },
      "noActor": {
        "title": "Inspection reassigned",
        "body": "The team reassigned the inspection for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Inspection reassigned",
        "body": "The team reassigned the inspection for {{request}}."
      },
      "toAssignee": {
        "title": "Inspection reassigned",
        "body": "{{actor}} reassigned the inspection for {{request}} to {{assignee}}."
      },
      "default": {
        "title": "Inspection reassigned",
        "body": "{{actor}} reassigned the inspection for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Inspection reassigned",
        "body": "{{actor}} reassigned the inspection for {{request}}."
      }
    },
    "asset_request_inspection_resumed": {
      "noActorReopened": {
        "title": "Inspection resumed",
        "body": "The team resumed the inspection for {{request}}. Inspection re-opened."
      },
      "reopened": {
        "title": "Inspection resumed",
        "body": "{{actor}} resumed the inspection for {{request}}. Inspection re-opened."
      },
      "noActor": {
        "title": "Inspection resumed",
        "body": "The team resumed the inspection for {{request}}."
      },
      "default": {
        "title": "Inspection resumed",
        "body": "{{actor}} resumed the inspection for {{request}}."
      }
    },
    "asset_request_inspection_started": {
      "noActor": {
        "title": "Inspection started",
        "body": "The team started the inspection for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Inspection started",
        "body": "The team started the inspection for {{request}}."
      },
      "default": {
        "title": "Inspection started",
        "body": "{{actor}} started the inspection for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Inspection started",
        "body": "{{actor}} started the inspection for {{request}}."
      }
    },
    "asset_request_job_cancelled": {
      "noActor": {
        "title": "Job cancelled",
        "body": "The team cancelled the job for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Job cancelled",
        "body": "The team cancelled the job for {{request}}."
      },
      "default": {
        "title": "Job cancelled",
        "body": "{{actor}} cancelled the job for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Job cancelled",
        "body": "{{actor}} cancelled the job for {{request}}."
      }
    },
    "asset_request_job_completed": {
      "noActorNoJobLegacy": {
        "title": "Job completed",
        "body": "The team completed the job for \"your request\". Review the work and confirm the fix."
      },
      "noActorNoJob": {
        "title": "Job completed",
        "body": "The team completed the job for your request. Review the work and confirm the fix."
      },
      "noJobLegacy": {
        "title": "Job completed",
        "body": "{{actor}} completed the job for \"your request\". Review the work and confirm the fix."
      },
      "noJob": {
        "title": "Job completed",
        "body": "{{actor}} completed the job for your request. Review the work and confirm the fix."
      },
      "noActor": {
        "title": "Job completed",
        "body": "The team completed the job for \"{{job}}\". Review the work and confirm the fix."
      },
      "default": {
        "title": "Job completed",
        "body": "{{actor}} completed the job for \"{{job}}\". Review the work and confirm the fix."
      }
    },
    "asset_request_job_created": {
      "noActorOutsideRepairsLegacy": {
        "title": "Job created from request",
        "body": "The team created a job for {{request}}. The team added a job outside the approved repairs: \"{{job}}\"."
      },
      "outsideRepairsLegacy": {
        "title": "Job created from request",
        "body": "{{actor}} created a job for {{request}}. The team added a job outside the approved repairs: \"{{job}}\"."
      },
      "noActorLegacy": {
        "title": "Job created from request",
        "body": "The team created a job for {{request}}. The team created a job for this request: \"{{job}}\"."
      },
      "noActorFromRepairLegacy": {
        "title": "Job created from request",
        "body": "The team created a job for {{request}}. Job created from an approved repair: \"{{job}}\"."
      },
      "noActorFollowUpLegacy": {
        "title": "Job created from request",
        "body": "The team created a job for {{request}}. The team proposed a follow-up job: \"{{job}}\"."
      },
      "defaultLegacy": {
        "title": "Job created from request",
        "body": "{{actor}} created a job for {{request}}. The team created a job for this request: \"{{job}}\"."
      },
      "fromRepairLegacy": {
        "title": "Job created from request",
        "body": "{{actor}} created a job for {{request}}. Job created from an approved repair: \"{{job}}\"."
      },
      "followUpLegacy": {
        "title": "Job created from request",
        "body": "{{actor}} created a job for {{request}}. The team proposed a follow-up job: \"{{job}}\"."
      },
      "noActorOutsideRepairs": {
        "title": "Job created from request",
        "body": "The team created a job for {{request}} outside the approved repairs: \"{{job}}\"."
      },
      "noActorFromRepair": {
        "title": "Job created from request",
        "body": "The team created a job for {{request}} from an approved repair: \"{{job}}\"."
      },
      "noActorFollowUp": {
        "title": "Job created from request",
        "body": "The team proposed a follow-up job for {{request}}: \"{{job}}\"."
      },
      "noActor": {
        "title": "Job created from request",
        "body": "The team created a job for {{request}}: \"{{job}}\"."
      },
      "outsideRepairs": {
        "title": "Job created from request",
        "body": "{{actor}} created a job for {{request}} outside the approved repairs: \"{{job}}\"."
      },
      "fromRepair": {
        "title": "Job created from request",
        "body": "{{actor}} created a job for {{request}} from an approved repair: \"{{job}}\"."
      },
      "followUp": {
        "title": "Job created from request",
        "body": "{{actor}} proposed a follow-up job for {{request}}: \"{{job}}\"."
      },
      "default": {
        "title": "Job created from request",
        "body": "{{actor}} created a job for {{request}}: \"{{job}}\"."
      }
    },
    "asset_request_job_paused": {
      "noActor": {
        "title": "Job paused",
        "body": "The team paused the job for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Job paused",
        "body": "The team paused the job for {{request}}."
      },
      "default": {
        "title": "Job paused",
        "body": "{{actor}} paused the job for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Job paused",
        "body": "{{actor}} paused the job for {{request}}."
      }
    },
    "asset_request_job_resumed": {
      "noActor": {
        "title": "Job resumed",
        "body": "The team resumed the job for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Job resumed",
        "body": "The team resumed the job for {{request}}."
      },
      "default": {
        "title": "Job resumed",
        "body": "{{actor}} resumed the job for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Job resumed",
        "body": "{{actor}} resumed the job for {{request}}."
      }
    },
    "asset_request_job_started": {
      "noActor": {
        "title": "Job started",
        "body": "The team started the job for {{request}}. {{note}}"
      },
      "noActorNoNote": {
        "title": "Job started",
        "body": "The team started the job for {{request}}."
      },
      "default": {
        "title": "Job started",
        "body": "{{actor}} started the job for {{request}}. {{note}}"
      },
      "noNote": {
        "title": "Job started",
        "body": "{{actor}} started the job for {{request}}."
      }
    },
    "asset_request_rejected": {
      "withNote": {
        "title": "Your request was declined",
        "body": "\"{{request}}\" was declined: {{note}}"
      },
      "default": {
        "title": "Your request was declined",
        "body": "\"{{request}}\" was declined."
      }
    },
    "asset_request_report_shared": {
      "forYouLegacy": {
        "title": "Inspection report shared",
        "body": "{{request}} report is ready for review. Inspection report shared for your review — {{findingCount}} finding(s) to action."
      },
      "defaultLegacy": {
        "title": "Inspection report shared",
        "body": "{{request}} report is ready for review. {{findingCount}} suggested repair finding(s) shared for review."
      },
      "forYouPlainLegacy": {
        "title": "Inspection report shared",
        "body": "{{request}} report is ready for review. Inspection report shared for your review."
      },
      "plainLegacy": {
        "title": "Inspection report shared",
        "body": "{{request}} report is ready for review. Inspection report shared for review."
      },
      "forYou": {
        "title": "Inspection report shared",
        "body": "{{request}} report is ready for your review: {{findingCount}} finding(s) to action."
      },
      "default": {
        "title": "Inspection report shared",
        "body": "{{request}} report is ready for review: {{findingCount}} suggested repair finding(s)."
      },
      "forYouPlain": {
        "title": "Inspection report shared",
        "body": "{{request}} report is ready for your review."
      },
      "plain": {
        "title": "Inspection report shared",
        "body": "{{request}} report is ready for review."
      }
    },
    "asset_request_requested_reschedule": {
      "visitNoInspectionNoPreference": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the inspection. No preferred time given."
      },
      "visitNoJobNoPreference": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the job. No preferred time given."
      },
      "visitNoInspection": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the inspection. Prefers {{preferredDate}} · {{period}} · {{note}}"
      },
      "visitNoInspectionNoNote": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the inspection. Prefers {{preferredDate}} · {{period}}"
      },
      "visitNoInspectionNoPeriod": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the inspection. Prefers {{preferredDate}} · {{note}}"
      },
      "visitNoPreference": {
        "title": "Different visit time requested",
        "body": "A different time was asked for {{work}}. No preferred time given."
      },
      "visitNoInspectionDateOnly": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the inspection. Prefers {{preferredDate}}"
      },
      "visitNoJob": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the job. Prefers {{preferredDate}} · {{period}} · {{note}}"
      },
      "visitNoJobNoNote": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the job. Prefers {{preferredDate}} · {{period}}"
      },
      "visitNoJobNoPeriod": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the job. Prefers {{preferredDate}} · {{note}}"
      },
      "visitNoInspectionNoDate": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the inspection. {{period}} · {{note}}"
      },
      "visitNoJobDateOnly": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the job. Prefers {{preferredDate}}"
      },
      "visit": {
        "title": "Different visit time requested",
        "body": "A different time was asked for {{work}}. Prefers {{preferredDate}} · {{period}} · {{note}}"
      },
      "visitNoInspectionPeriodOnly": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the inspection. {{period}}"
      },
      "visitNoInspectionNoteOnly": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the inspection. {{note}}"
      },
      "visitNoNote": {
        "title": "Different visit time requested",
        "body": "A different time was asked for {{work}}. Prefers {{preferredDate}} · {{period}}"
      },
      "visitNoPeriod": {
        "title": "Different visit time requested",
        "body": "A different time was asked for {{work}}. Prefers {{preferredDate}} · {{note}}"
      },
      "visitNoJobNoDate": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the job. {{period}} · {{note}}"
      },
      "requestNoRequest": {
        "title": "New time requested",
        "body": "Request — the requester asked for a different time. ({{preferredDate}} {{periodLabel}})"
      },
      "requestNoRequestNoPeriod": {
        "title": "New time requested",
        "body": "Request — the requester asked for a different time. ({{preferredDate}})"
      },
      "visitDateOnly": {
        "title": "Different visit time requested",
        "body": "A different time was asked for {{work}}. Prefers {{preferredDate}}"
      },
      "visitNoJobPeriodOnly": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the job. {{period}}"
      },
      "visitNoJobNoteOnly": {
        "title": "Different visit time requested",
        "body": "A different time was asked for the job. {{note}}"
      },
      "requestNoRequestNoPreference": {
        "title": "New time requested",
        "body": "Request — the requester asked for a different time."
      },
      "visitNoDate": {
        "title": "Different visit time requested",
        "body": "A different time was asked for {{work}}. {{period}} · {{note}}"
      },
      "request": {
        "title": "New time requested",
        "body": "{{request}} — the requester asked for a different time. ({{preferredDate}} {{periodLabel}})"
      },
      "requestNoPeriod": {
        "title": "New time requested",
        "body": "{{request}} — the requester asked for a different time. ({{preferredDate}})"
      },
      "visitPeriodOnly": {
        "title": "Different visit time requested",
        "body": "A different time was asked for {{work}}. {{period}}"
      },
      "visitNoteOnly": {
        "title": "Different visit time requested",
        "body": "A different time was asked for {{work}}. {{note}}"
      },
      "requestNoPreference": {
        "title": "New time requested",
        "body": "{{request}} — the requester asked for a different time."
      }
    },
    "asset_request_requester_cancelled": {
      "noRequest": {
        "title": "Request withdrawn",
        "body": "Request was withdrawn by the requester. {{reason}}"
      },
      "noRequestNoReason": {
        "title": "Request withdrawn",
        "body": "Request was withdrawn by the requester."
      },
      "default": {
        "title": "Request withdrawn",
        "body": "{{request}} was withdrawn by the requester. {{reason}}"
      },
      "noReason": {
        "title": "Request withdrawn",
        "body": "{{request}} was withdrawn by the requester."
      }
    },
    "asset_request_response_received": {
      "approvedLegacy": {
        "title": "Report response received",
        "body": "{{request}} has a report response. Inspection report response approved."
      },
      "rejectedLegacy": {
        "title": "Report response received",
        "body": "{{request}} has a report response. Inspection report response rejected."
      },
      "defaultLegacy": {
        "title": "Report response received",
        "body": "{{request}} has a report response. {{reason}}"
      },
      "approved": {
        "title": "Report response received",
        "body": "{{request}} report was approved."
      },
      "rejected": {
        "title": "Report response received",
        "body": "{{request}} report was rejected."
      },
      "default": {
        "title": "Report response received",
        "body": "{{request}} report was rejected. {{reason}}"
      }
    },
    "asset_request_rework_requested": {
      "requesterNoRequesterNoRequestNoReasonLegacy": {
        "title": "Rework requested",
        "body": "The requester asked for rework on \"the request\": Rework requested by requester"
      },
      "requesterNoRequesterNoReasonLegacy": {
        "title": "Rework requested",
        "body": "The requester asked for rework on \"{{request}}\": Rework requested by requester"
      },
      "requesterNoRequestNoReasonLegacy": {
        "title": "Rework requested",
        "body": "{{requester}} asked for rework on \"the request\": Rework requested by requester"
      },
      "requesterNoReasonLegacy": {
        "title": "Rework requested",
        "body": "{{requester}} asked for rework on \"{{request}}\": Rework requested by requester"
      },
      "requesterNoRequesterNoRequestLegacy": {
        "title": "Rework requested",
        "body": "The requester asked for rework on \"the request\": {{reason}}"
      },
      "requesterNoRequestLegacy": {
        "title": "Rework requested",
        "body": "{{requester}} asked for rework on \"the request\": {{reason}}"
      },
      "requesterNoRequesterNoRequestNoReason": {
        "title": "Rework requested",
        "body": "The requester asked for rework on the request."
      },
      "requesterNoRequesterNoReason": {
        "title": "Rework requested",
        "body": "The requester asked for rework on \"{{request}}\"."
      },
      "requesterNoRequestNoReason": {
        "title": "Rework requested",
        "body": "{{requester}} asked for rework on the request."
      },
      "requesterNoReason": {
        "title": "Rework requested",
        "body": "{{requester}} asked for rework on \"{{request}}\"."
      },
      "requesterNoRequesterNoRequest": {
        "title": "Rework requested",
        "body": "The requester asked for rework on the request: {{reason}}"
      },
      "requesterNoRequester": {
        "title": "Rework requested",
        "body": "The requester asked for rework on \"{{request}}\": {{reason}}"
      },
      "requesterNoRequest": {
        "title": "Rework requested",
        "body": "{{requester}} asked for rework on the request: {{reason}}"
      },
      "teamNoActor": {
        "title": "Rework requested",
        "body": "The team asked for rework on {{request}}. {{note}}"
      },
      "teamNoActorNoNote": {
        "title": "Rework requested",
        "body": "The team asked for rework on {{request}}."
      },
      "requester": {
        "title": "Rework requested",
        "body": "{{requester}} asked for rework on \"{{request}}\": {{reason}}"
      },
      "team": {
        "title": "Rework requested",
        "body": "{{actor}} asked for rework on {{request}}. {{note}}"
      },
      "teamNoNote": {
        "title": "Rework requested",
        "body": "{{actor}} asked for rework on {{request}}."
      }
    },
    "asset_request_scheduled": {
      "scheduled": {
        "title": "Your request was scheduled",
        "body": "{{request}} is scheduled for {{scheduledDate}}."
      },
      "rescheduled": {
        "title": "Your visit was rescheduled",
        "body": "{{request}} — new date {{newWhen}}."
      }
    },
    "asset_retired": {
      "noAsset": {
        "title": "Asset retired",
        "body": "An asset has been retired ({{reason}}). It takes no new inspections or jobs. {{note}}"
      },
      "noAssetNoNote": {
        "title": "Asset retired",
        "body": "An asset has been retired ({{reason}}). It takes no new inspections or jobs."
      },
      "noAssetNoReason": {
        "title": "Asset retired",
        "body": "An asset has been retired. It takes no new inspections or jobs. {{note}}"
      },
      "noAssetNoReasonNoNote": {
        "title": "Asset retired",
        "body": "An asset has been retired. It takes no new inspections or jobs."
      },
      "default": {
        "title": "Asset retired",
        "body": "{{asset}} has been retired ({{reason}}). It takes no new inspections or jobs. {{note}}"
      },
      "noNote": {
        "title": "Asset retired",
        "body": "{{asset}} has been retired ({{reason}}). It takes no new inspections or jobs."
      },
      "noReason": {
        "title": "Asset retired",
        "body": "{{asset}} has been retired. It takes no new inspections or jobs. {{note}}"
      },
      "noReasonNoNote": {
        "title": "Asset retired",
        "body": "{{asset}} has been retired. It takes no new inspections or jobs."
      }
    },
    "asset_share_accepted": {
      "noContactNoAsset": {
        "title": "Asset share accepted",
        "body": "A contact accepted access to the asset."
      },
      "noAsset": {
        "title": "Asset share accepted",
        "body": "{{contact}} accepted access to the asset."
      },
      "noContact": {
        "title": "Asset share accepted",
        "body": "A contact accepted access to {{asset}}."
      },
      "default": {
        "title": "Asset share accepted",
        "body": "{{contact}} accepted access to {{asset}}."
      }
    },
    "asset_share_declined": {
      "noContactNoAsset": {
        "title": "Asset share declined",
        "body": "A contact declined access to the asset."
      },
      "noAsset": {
        "title": "Asset share declined",
        "body": "{{contact}} declined access to the asset."
      },
      "noContact": {
        "title": "Asset share declined",
        "body": "A contact declined access to {{asset}}."
      },
      "default": {
        "title": "Asset share declined",
        "body": "{{contact}} declined access to {{asset}}."
      }
    },
    "asset_share_ended": {
      "noAsset": {
        "title": "Shared asset retired",
        "body": "An asset has been retired by its owner, so your access to it has ended. Anything you reported on it stays on their record."
      },
      "default": {
        "title": "Shared asset retired",
        "body": "{{asset}} has been retired by its owner, so your access to it has ended. Anything you reported on it stays on their record."
      }
    },
    "asset_team_assigned": {
      "noTeam": {
        "title": "New asset in your team",
        "body": "{{asset}} has been assigned to your team."
      },
      "default": {
        "title": "New asset in your team",
        "body": "{{asset}} has been assigned to {{team}}."
      }
    },
    "asset_team_removed": {
      "default": {
        "title": "Asset moved from your team",
        "body": "{{asset}} is no longer assigned to your team."
      }
    },
    "automation_notice": {
      "default": {
        "title": "{{title}}",
        "body": "{{body}}"
      }
    },
    "completion_submitted": {
      "requesterFirstNoActorNoJobNoRequesterLegacy": {
        "title": "Job completed — the requester confirms first",
        "body": "A colleague completed \"a job\". The requester confirms the fix first; you verify after."
      },
      "requesterFirstNoActorNoJobNoRequester": {
        "title": "Job completed — the requester confirms first",
        "body": "A colleague completed a job. The requester confirms the fix first; you verify after."
      },
      "requesterFirstNoActorNoRequester": {
        "title": "Job completed — the requester confirms first",
        "body": "A colleague completed \"{{job}}\". The requester confirms the fix first; you verify after."
      },
      "requesterFirstNoJobNoRequesterLegacy": {
        "title": "Job completed — the requester confirms first",
        "body": "{{actor}} completed \"a job\". The requester confirms the fix first; you verify after."
      },
      "requesterFirstNoJobNoRequester": {
        "title": "Job completed — the requester confirms first",
        "body": "{{actor}} completed a job. The requester confirms the fix first; you verify after."
      },
      "requesterFirstNoActorNoJobLegacy": {
        "title": "Job completed — the requester confirms first",
        "body": "A colleague completed \"a job\". {{requester}} confirms the fix first; you verify after."
      },
      "requesterFirstNoActorNoJob": {
        "title": "Job completed — the requester confirms first",
        "body": "A colleague completed a job. {{requester}} confirms the fix first; you verify after."
      },
      "requesterFirstNoRequester": {
        "title": "Job completed — the requester confirms first",
        "body": "{{actor}} completed \"{{job}}\". The requester confirms the fix first; you verify after."
      },
      "requesterFirstNoActor": {
        "title": "Job completed — the requester confirms first",
        "body": "A colleague completed \"{{job}}\". {{requester}} confirms the fix first; you verify after."
      },
      "requesterFirstNoJobLegacy": {
        "title": "Job completed — the requester confirms first",
        "body": "{{actor}} completed \"a job\". {{requester}} confirms the fix first; you verify after."
      },
      "requesterFirstNoJob": {
        "title": "Job completed — the requester confirms first",
        "body": "{{actor}} completed a job. {{requester}} confirms the fix first; you verify after."
      },
      "requesterFirst": {
        "title": "Job completed — the requester confirms first",
        "body": "{{actor}} completed \"{{job}}\". {{requester}} confirms the fix first; you verify after."
      },
      "forApprovalSelf": {
        "title": "Your completion is ready to verify",
        "body": "You completed \"{{job}}\". Verify it to close the job."
      },
      "forApprovalSelfNoJob": {
        "title": "Your completion is ready to verify",
        "body": "You completed a job. Verify it to close the job."
      },
      "forApprovalNoActorNoJobLegacy": {
        "title": "Job completion submitted for approval",
        "body": "A colleague completed \"a job\". Review it and approve."
      },
      "forApprovalNoActorNoJob": {
        "title": "Job completion submitted for approval",
        "body": "A colleague completed a job. Review it and approve."
      },
      "forApprovalNoActor": {
        "title": "Job completion submitted for approval",
        "body": "A colleague completed \"{{job}}\". Review it and approve."
      },
      "forApprovalNoJobLegacy": {
        "title": "Job completion submitted for approval",
        "body": "{{actor}} completed \"a job\". Review it and approve."
      },
      "forApprovalNoJob": {
        "title": "Job completion submitted for approval",
        "body": "{{actor}} completed a job. Review it and approve."
      },
      "forApproval": {
        "title": "Job completion submitted for approval",
        "body": "{{actor}} completed \"{{job}}\". Review it and approve."
      }
    },
    "document_expiring": {
      "certificateNoDocument": {
        "title": "Certificate expiring soon",
        "body": "A document · {{asset}} expires {{expiryDate}}."
      },
      "certificateNoDocumentNoAsset": {
        "title": "Certificate expiring soon",
        "body": "A document expires {{expiryDate}}."
      },
      "documentNoDocument": {
        "title": "Document expiring soon",
        "body": "A document · {{asset}} expires {{expiryDate}}."
      },
      "documentNoDocumentNoAsset": {
        "title": "Document expiring soon",
        "body": "A document expires {{expiryDate}}."
      },
      "certificate": {
        "title": "Certificate expiring soon",
        "body": "{{document}} · {{asset}} expires {{expiryDate}}."
      },
      "certificateNoAsset": {
        "title": "Certificate expiring soon",
        "body": "{{document}} expires {{expiryDate}}."
      },
      "document": {
        "title": "Document expiring soon",
        "body": "{{document}} · {{asset}} expires {{expiryDate}}."
      },
      "documentNoAsset": {
        "title": "Document expiring soon",
        "body": "{{document}} expires {{expiryDate}}."
      }
    },
    "document_rejected": {
      "noValidatorNoAsset": {
        "title": "Document rejected",
        "body": "The validator rejected \"{{document}}\" for an asset. Reason: {{reason}}"
      },
      "noValidator": {
        "title": "Document rejected",
        "body": "The validator rejected \"{{document}}\" for {{asset}}. Reason: {{reason}}"
      },
      "noValidatorNoAssetNoReason": {
        "title": "Document rejected",
        "body": "The validator rejected \"{{document}}\" for an asset."
      },
      "noAsset": {
        "title": "Document rejected",
        "body": "{{validator}} rejected \"{{document}}\" for an asset. Reason: {{reason}}"
      },
      "noValidatorNoReason": {
        "title": "Document rejected",
        "body": "The validator rejected \"{{document}}\" for {{asset}}."
      },
      "default": {
        "title": "Document rejected",
        "body": "{{validator}} rejected \"{{document}}\" for {{asset}}. Reason: {{reason}}"
      },
      "noAssetNoReason": {
        "title": "Document rejected",
        "body": "{{validator}} rejected \"{{document}}\" for an asset."
      },
      "noReason": {
        "title": "Document rejected",
        "body": "{{validator}} rejected \"{{document}}\" for {{asset}}."
      }
    },
    "document_validated": {
      "noValidatorNoAsset": {
        "title": "Document validated",
        "body": "The validator validated \"{{document}}\" for an asset."
      },
      "noValidator": {
        "title": "Document validated",
        "body": "The validator validated \"{{document}}\" for {{asset}}."
      },
      "noAsset": {
        "title": "Document validated",
        "body": "{{validator}} validated \"{{document}}\" for an asset."
      },
      "default": {
        "title": "Document validated",
        "body": "{{validator}} validated \"{{document}}\" for {{asset}}."
      }
    },
    "document_validation_requested": {
      "reminderNoRequesterNoAsset": {
        "title": "Reminder: validate \"{{document}}\"",
        "body": "A colleague reminded you to validate this {{documentType}} for an asset (expires {{expiryDate}})."
      },
      "reminderNoRequester": {
        "title": "Reminder: validate \"{{document}}\"",
        "body": "A colleague reminded you to validate this {{documentType}} for {{asset}} (expires {{expiryDate}})."
      },
      "reminderNoAsset": {
        "title": "Reminder: validate \"{{document}}\"",
        "body": "{{requester}} reminded you to validate this {{documentType}} for an asset (expires {{expiryDate}})."
      },
      "reminderNoRequesterNoAssetNoExpiry": {
        "title": "Reminder: validate \"{{document}}\"",
        "body": "A colleague reminded you to validate this {{documentType}} for an asset."
      },
      "requestNoRequesterNoAsset": {
        "title": "Validate \"{{document}}\"",
        "body": "A colleague asked you to validate this {{documentType}} for an asset (expires {{expiryDate}})."
      },
      "reminder": {
        "title": "Reminder: validate \"{{document}}\"",
        "body": "{{requester}} reminded you to validate this {{documentType}} for {{asset}} (expires {{expiryDate}})."
      },
      "reminderNoRequesterNoExpiry": {
        "title": "Reminder: validate \"{{document}}\"",
        "body": "A colleague reminded you to validate this {{documentType}} for {{asset}}."
      },
      "requestNoRequester": {
        "title": "Validate \"{{document}}\"",
        "body": "A colleague asked you to validate this {{documentType}} for {{asset}} (expires {{expiryDate}})."
      },
      "reminderNoAssetNoExpiry": {
        "title": "Reminder: validate \"{{document}}\"",
        "body": "{{requester}} reminded you to validate this {{documentType}} for an asset."
      },
      "requestNoAsset": {
        "title": "Validate \"{{document}}\"",
        "body": "{{requester}} asked you to validate this {{documentType}} for an asset (expires {{expiryDate}})."
      },
      "requestNoRequesterNoAssetNoExpiry": {
        "title": "Validate \"{{document}}\"",
        "body": "A colleague asked you to validate this {{documentType}} for an asset."
      },
      "reminderNoExpiry": {
        "title": "Reminder: validate \"{{document}}\"",
        "body": "{{requester}} reminded you to validate this {{documentType}} for {{asset}}."
      },
      "request": {
        "title": "Validate \"{{document}}\"",
        "body": "{{requester}} asked you to validate this {{documentType}} for {{asset}} (expires {{expiryDate}})."
      },
      "requestNoRequesterNoExpiry": {
        "title": "Validate \"{{document}}\"",
        "body": "A colleague asked you to validate this {{documentType}} for {{asset}}."
      },
      "requestNoAssetNoExpiry": {
        "title": "Validate \"{{document}}\"",
        "body": "{{requester}} asked you to validate this {{documentType}} for an asset."
      },
      "requestNoExpiry": {
        "title": "Validate \"{{document}}\"",
        "body": "{{requester}} asked you to validate this {{documentType}} for {{asset}}."
      },
      "uploadedByYou": {
        "title": "Validate \"{{document}}\"",
        "body": "You uploaded this {{documentType}} for {{asset}} (expires {{expiryDate}})."
      },
      "uploadedByColleague": {
        "title": "Validate \"{{document}}\"",
        "body": "A colleague uploaded this {{documentType}} for {{asset}} (expires {{expiryDate}})."
      },
      "uploaded": {
        "title": "Validate \"{{document}}\"",
        "body": "{{uploader}} uploaded this {{documentType}} for {{asset}} (expires {{expiryDate}})."
      },
      "uploadedByYouNoExpiry": {
        "title": "Validate \"{{document}}\"",
        "body": "You uploaded this {{documentType}} for {{asset}}."
      },
      "uploadedByColleagueNoExpiry": {
        "title": "Validate \"{{document}}\"",
        "body": "A colleague uploaded this {{documentType}} for {{asset}}."
      },
      "uploadedNoExpiry": {
        "title": "Validate \"{{document}}\"",
        "body": "{{uploader}} uploaded this {{documentType}} for {{asset}}."
      }
    },
    "draft_created": {
      "noAssetLegacy": {
        "title": "Draft inspection created",
        "body": "Scheduled inspection for Asset - {{template}} is ready to complete."
      },
      "noAsset": {
        "title": "Draft inspection created",
        "body": "Scheduled inspection for an asset - {{template}} is ready to complete."
      },
      "default": {
        "title": "Draft inspection created",
        "body": "Scheduled inspection for {{asset}} - {{template}} is ready to complete."
      }
    },
    "evidence_requested": {
      "noActorNoJobLegacy": {
        "title": "More evidence needed on your job",
        "body": "The reviewer asked for more evidence on \"a job\". {{reason}}"
      },
      "noActorNoJob": {
        "title": "More evidence needed on your job",
        "body": "The reviewer asked for more evidence on a job. {{reason}}"
      },
      "noActorNoJobNoReasonLegacy": {
        "title": "More evidence needed on your job",
        "body": "The reviewer asked for more evidence on \"a job\"."
      },
      "noActorNoJobNoReason": {
        "title": "More evidence needed on your job",
        "body": "The reviewer asked for more evidence on a job."
      },
      "noActor": {
        "title": "More evidence needed on your job",
        "body": "The reviewer asked for more evidence on \"{{job}}\". {{reason}}"
      },
      "noActorNoReason": {
        "title": "More evidence needed on your job",
        "body": "The reviewer asked for more evidence on \"{{job}}\"."
      },
      "noJobLegacy": {
        "title": "More evidence needed on your job",
        "body": "{{actor}} asked for more evidence on \"a job\". {{reason}}"
      },
      "noJob": {
        "title": "More evidence needed on your job",
        "body": "{{actor}} asked for more evidence on a job. {{reason}}"
      },
      "noJobNoReasonLegacy": {
        "title": "More evidence needed on your job",
        "body": "{{actor}} asked for more evidence on \"a job\"."
      },
      "noJobNoReason": {
        "title": "More evidence needed on your job",
        "body": "{{actor}} asked for more evidence on a job."
      },
      "default": {
        "title": "More evidence needed on your job",
        "body": "{{actor}} asked for more evidence on \"{{job}}\". {{reason}}"
      },
      "noReason": {
        "title": "More evidence needed on your job",
        "body": "{{actor}} asked for more evidence on \"{{job}}\"."
      }
    },
    "free_period_ending": {
      "ended": {
        "title": "Your free period has ended",
        "body": "Your workspace is now read-only. Choose a plan to keep working. Nothing has been deleted."
      },
      "tomorrow": {
        "title": "Your free period ends tomorrow",
        "body": "Choose a plan before {{endDate}} to keep adding and editing. Nothing is deleted either way."
      },
      "inDays": {
        "title": "Your free period ends in {{daysLeft}} days",
        "body": "Choose a plan before {{endDate}} to keep adding and editing. Nothing is deleted either way."
      }
    },
    "inspection_approved": {
      "noActorNoAsset": {
        "title": "Your inspection was approved",
        "body": "The approver approved your inspection on an asset."
      },
      "noActor": {
        "title": "Your inspection was approved",
        "body": "The approver approved your inspection on {{asset}}."
      },
      "noAsset": {
        "title": "Your inspection was approved",
        "body": "{{actor}} approved your inspection on an asset."
      },
      "default": {
        "title": "Your inspection was approved",
        "body": "{{actor}} approved your inspection on {{asset}}."
      }
    },
    "inspection_assigned": {
      "scheduledByActorNoTemplateNoAsset": {
        "title": "Inspection assigned to you",
        "body": "{{actor}} assigned you an inspection on an asset · {{when}}."
      },
      "byActorNoTemplateNoAsset": {
        "title": "Inspection assigned to you",
        "body": "{{actor}} assigned you an inspection on an asset."
      },
      "scheduledByActorNoTemplate": {
        "title": "Inspection assigned to you",
        "body": "{{actor}} assigned you an inspection on {{asset}} · {{when}}."
      },
      "byActorNoTemplate": {
        "title": "Inspection assigned to you",
        "body": "{{actor}} assigned you an inspection on {{asset}}."
      },
      "scheduledByActorNoAsset": {
        "title": "Inspection assigned to you",
        "body": "{{actor}} assigned you \"{{template}}\" on an asset · {{when}}."
      },
      "byActorNoAsset": {
        "title": "Inspection assigned to you",
        "body": "{{actor}} assigned you \"{{template}}\" on an asset."
      },
      "scheduledNoTemplateNoAsset": {
        "title": "Inspection assigned to you",
        "body": "Inspection · An asset · {{when}}."
      },
      "scheduledByActor": {
        "title": "Inspection assigned to you",
        "body": "{{actor}} assigned you \"{{template}}\" on {{asset}} · {{when}}."
      },
      "noTemplateNoAsset": {
        "title": "Inspection assigned to you",
        "body": "Inspection · An asset."
      },
      "byActor": {
        "title": "Inspection assigned to you",
        "body": "{{actor}} assigned you \"{{template}}\" on {{asset}}."
      },
      "scheduledNoTemplate": {
        "title": "Inspection assigned to you",
        "body": "Inspection · {{asset}} · {{when}}."
      },
      "scheduledNoAsset": {
        "title": "Inspection assigned to you",
        "body": "{{template}} · An asset · {{when}}."
      },
      "noTemplate": {
        "title": "Inspection assigned to you",
        "body": "Inspection · {{asset}}."
      },
      "noAsset": {
        "title": "Inspection assigned to you",
        "body": "{{template}} · An asset."
      },
      "scheduled": {
        "title": "Inspection assigned to you",
        "body": "{{template}} · {{asset}} · {{when}}."
      },
      "default": {
        "title": "Inspection assigned to you",
        "body": "{{template}} · {{asset}}."
      }
    },
    "inspection_completed_for_approval": {
      "noActorNoAsset": {
        "title": "Inspection ready for your approval",
        "body": "A colleague completed an inspection on an asset. Review it and approve."
      },
      "noActor": {
        "title": "Inspection ready for your approval",
        "body": "A colleague completed an inspection on {{asset}}. Review it and approve."
      },
      "noAsset": {
        "title": "Inspection ready for your approval",
        "body": "{{actor}} completed an inspection on an asset. Review it and approve."
      },
      "default": {
        "title": "Inspection ready for your approval",
        "body": "{{actor}} completed an inspection on {{asset}}. Review it and approve."
      }
    },
    "inspection_reassigned": {
      "inspectionNoAsset": {
        "title": "Inspection reassigned",
        "body": "You are no longer assigned to the inspection for this asset."
      },
      "inspection": {
        "title": "Inspection reassigned",
        "body": "You are no longer assigned to the inspection for {{asset}}."
      },
      "scheduleNoAsset": {
        "title": "Inspection reassigned",
        "body": "You are no longer assigned to this inspection."
      },
      "schedule": {
        "title": "Inspection reassigned",
        "body": "You are no longer assigned to {{asset}}."
      }
    },
    "inspection_reopened": {
      "noActorNoAsset": {
        "title": "Your inspection was re-opened",
        "body": "A colleague re-opened your inspection on an asset."
      },
      "noActor": {
        "title": "Your inspection was re-opened",
        "body": "A colleague re-opened your inspection on {{asset}}."
      },
      "noAsset": {
        "title": "Your inspection was re-opened",
        "body": "{{actor}} re-opened your inspection on an asset."
      },
      "default": {
        "title": "Your inspection was re-opened",
        "body": "{{actor}} re-opened your inspection on {{asset}}."
      }
    },
    "inspection_report_shared": {
      "recipientNoSharerNoAsset": {
        "title": "Inspection report ready",
        "body": "The inspector shared the inspection report for an asset. Review it and sign off."
      },
      "recipientNoSharer": {
        "title": "Inspection report ready",
        "body": "The inspector shared the inspection report for {{asset}}. Review it and sign off."
      },
      "recipientNoAsset": {
        "title": "Inspection report ready",
        "body": "{{sharer}} shared the inspection report for an asset. Review it and sign off."
      },
      "recipient": {
        "title": "Inspection report ready",
        "body": "{{sharer}} shared the inspection report for {{asset}}. Review it and sign off."
      },
      "sharerNoRecipient": {
        "title": "Inspection report shared",
        "body": "Review link ready for Recipient."
      },
      "resentNoRecipient": {
        "title": "Inspection report resent",
        "body": "Sent again to Recipient."
      },
      "sharer": {
        "title": "Inspection report shared",
        "body": "Review link ready for {{recipient}}."
      },
      "sharerBlankName": {
        "title": "Inspection report shared",
        "body": "Review link is ready."
      },
      "resentBlankName": {
        "title": "Inspection report resent",
        "body": "Report link updated."
      },
      "resent": {
        "title": "Inspection report resent",
        "body": "Sent again to {{recipient}}."
      }
    },
    "inspection_sent_back": {
      "noActorNoAsset": {
        "title": "Your inspection was sent back",
        "body": "Your approver sent back your inspection on an asset: {{reason}}"
      },
      "noActor": {
        "title": "Your inspection was sent back",
        "body": "Your approver sent back your inspection on {{asset}}: {{reason}}"
      },
      "noAsset": {
        "title": "Your inspection was sent back",
        "body": "{{actor}} sent back your inspection on an asset: {{reason}}"
      },
      "default": {
        "title": "Your inspection was sent back",
        "body": "{{actor}} sent back your inspection on {{asset}}: {{reason}}"
      }
    },
    "inspection_unassigned": {
      "inspectionNoAsset": {
        "title": "Inspection unassigned",
        "body": "You are no longer assigned to the inspection for this asset."
      },
      "inspection": {
        "title": "Inspection unassigned",
        "body": "You are no longer assigned to the inspection for {{asset}}."
      },
      "scheduleNoAsset": {
        "title": "Inspection unassigned",
        "body": "You are no longer assigned to this inspection."
      },
      "schedule": {
        "title": "Inspection unassigned",
        "body": "You are no longer assigned to {{asset}}."
      }
    },
    "inspection_visit_proposed": {
      "noInspection": {
        "title": "Confirm the visit time",
        "body": "An inspection is planned for {{when}} — confirm someone can be there, or ask for a different time."
      },
      "default": {
        "title": "Confirm the visit time",
        "body": "{{inspection}} is planned for {{when}} — confirm someone can be there, or ask for a different time."
      }
    },
    "job_accepted": {
      "noActorNoJobLegacy": {
        "title": "Job accepted",
        "body": "The assignee accepted \"a job\"."
      },
      "noActorNoJob": {
        "title": "Job accepted",
        "body": "The assignee accepted a job."
      },
      "noActor": {
        "title": "Job accepted",
        "body": "The assignee accepted \"{{job}}\"."
      },
      "noJobLegacy": {
        "title": "Job accepted",
        "body": "{{actor}} accepted \"a job\"."
      },
      "noJob": {
        "title": "Job accepted",
        "body": "{{actor}} accepted a job."
      },
      "default": {
        "title": "Job accepted",
        "body": "{{actor}} accepted \"{{job}}\"."
      }
    },
    "job_approver_assigned": {
      "noJob": {
        "title": "Approval responsibility assigned",
        "body": "A job will require your review."
      },
      "default": {
        "title": "Approval responsibility assigned",
        "body": "{{job}} will require your review."
      }
    },
    "job_approver_removed": {
      "noJobLegacy": {
        "title": "Approval responsibility changed",
        "body": "You are no longer the approver for A job."
      },
      "noJob": {
        "title": "Approval responsibility changed",
        "body": "You are no longer the approver for a job."
      },
      "default": {
        "title": "Approval responsibility changed",
        "body": "You are no longer the approver for {{job}}."
      }
    },
    "job_assigned": {
      "byActorNoJobLegacy": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you \"A job\" · {{asset}} · due {{dueDate}}."
      },
      "byActorNoJob": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you a job · {{asset}} · due {{dueDate}}."
      },
      "byActorNoJobNoAssetLegacy": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you \"A job\" · due {{dueDate}}."
      },
      "byActorNoJobNoAsset": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you a job · due {{dueDate}}."
      },
      "byActor": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you \"{{job}}\" · {{asset}} · due {{dueDate}}."
      },
      "byActorNoJobNoDueLegacy": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you \"A job\" · {{asset}}."
      },
      "byActorNoJobNoDue": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you a job · {{asset}}."
      },
      "byActorNoAsset": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you \"{{job}}\" · due {{dueDate}}."
      },
      "byActorNoJobNoAssetNoDueLegacy": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you \"A job\"."
      },
      "byActorNoJobNoAssetNoDue": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you a job."
      },
      "byActorNoDue": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you \"{{job}}\" · {{asset}}."
      },
      "byActorNoAssetNoDue": {
        "title": "Job assigned to you",
        "body": "{{actor}} assigned you \"{{job}}\"."
      },
      "noJob": {
        "title": "Job assigned to you",
        "body": "A job · {{asset}} · due {{dueDate}}."
      },
      "noJobNoAsset": {
        "title": "Job assigned to you",
        "body": "A job · due {{dueDate}}."
      },
      "default": {
        "title": "Job assigned to you",
        "body": "{{job}} · {{asset}} · due {{dueDate}}."
      },
      "noJobNoDue": {
        "title": "Job assigned to you",
        "body": "A job · {{asset}}."
      },
      "noAsset": {
        "title": "Job assigned to you",
        "body": "{{job}} · due {{dueDate}}."
      },
      "noJobNoAssetNoDue": {
        "title": "Job assigned to you",
        "body": "A job."
      },
      "noDue": {
        "title": "Job assigned to you",
        "body": "{{job}} · {{asset}}."
      },
      "noAssetNoDue": {
        "title": "Job assigned to you",
        "body": "{{job}}."
      }
    },
    "job_cancelled": {
      "noActorNoJobLegacy": {
        "title": "Job cancelled",
        "body": "A colleague cancelled \"a job\" on {{asset}}."
      },
      "noActorNoJob": {
        "title": "Job cancelled",
        "body": "A colleague cancelled a job on {{asset}}."
      },
      "noActorNoJobNoAssetLegacy": {
        "title": "Job cancelled",
        "body": "A colleague cancelled \"a job\"."
      },
      "noActorNoJobNoAsset": {
        "title": "Job cancelled",
        "body": "A colleague cancelled a job."
      },
      "noActor": {
        "title": "Job cancelled",
        "body": "A colleague cancelled \"{{job}}\" on {{asset}}."
      },
      "noActorNoAsset": {
        "title": "Job cancelled",
        "body": "A colleague cancelled \"{{job}}\"."
      },
      "noJobLegacy": {
        "title": "Job cancelled",
        "body": "{{actor}} cancelled \"a job\" on {{asset}}."
      },
      "noJob": {
        "title": "Job cancelled",
        "body": "{{actor}} cancelled a job on {{asset}}."
      },
      "noJobNoAssetLegacy": {
        "title": "Job cancelled",
        "body": "{{actor}} cancelled \"a job\"."
      },
      "noJobNoAsset": {
        "title": "Job cancelled",
        "body": "{{actor}} cancelled a job."
      },
      "default": {
        "title": "Job cancelled",
        "body": "{{actor}} cancelled \"{{job}}\" on {{asset}}."
      },
      "noAsset": {
        "title": "Job cancelled",
        "body": "{{actor}} cancelled \"{{job}}\"."
      }
    },
    "job_completion_approved": {
      "noRequesterNoJob": {
        "title": "Completion approved",
        "body": "The requester approved the completion of the job."
      },
      "noRequester": {
        "title": "Completion approved",
        "body": "The requester approved the completion of {{job}}."
      },
      "noJob": {
        "title": "Completion approved",
        "body": "{{requester}} approved the completion of the job."
      },
      "default": {
        "title": "Completion approved",
        "body": "{{requester}} approved the completion of {{job}}."
      }
    },
    "job_declined": {
      "noActorNoJobLegacy": {
        "title": "Job declined — reassign it",
        "body": "The assignee declined \"a job\". Reason: {{reason}}. Assign it to someone else to keep it moving."
      },
      "noActorNoJob": {
        "title": "Job declined — reassign it",
        "body": "The assignee declined a job. Reason: {{reason}} Assign it to someone else to keep it moving."
      },
      "noActor": {
        "title": "Job declined — reassign it",
        "body": "The assignee declined \"{{job}}\". Reason: {{reason}} Assign it to someone else to keep it moving."
      },
      "noActorNoJobNoReasonLegacy": {
        "title": "Job declined — reassign it",
        "body": "The assignee declined \"a job\". Assign it to someone else to keep it moving."
      },
      "noActorNoJobNoReason": {
        "title": "Job declined — reassign it",
        "body": "The assignee declined a job. Assign it to someone else to keep it moving."
      },
      "noJobLegacy": {
        "title": "Job declined — reassign it",
        "body": "{{actor}} declined \"a job\". Reason: {{reason}}. Assign it to someone else to keep it moving."
      },
      "noJob": {
        "title": "Job declined — reassign it",
        "body": "{{actor}} declined a job. Reason: {{reason}} Assign it to someone else to keep it moving."
      },
      "noActorNoReason": {
        "title": "Job declined — reassign it",
        "body": "The assignee declined \"{{job}}\". Assign it to someone else to keep it moving."
      },
      "default": {
        "title": "Job declined — reassign it",
        "body": "{{actor}} declined \"{{job}}\". Reason: {{reason}} Assign it to someone else to keep it moving."
      },
      "noJobNoReasonLegacy": {
        "title": "Job declined — reassign it",
        "body": "{{actor}} declined \"a job\". Assign it to someone else to keep it moving."
      },
      "noJobNoReason": {
        "title": "Job declined — reassign it",
        "body": "{{actor}} declined a job. Assign it to someone else to keep it moving."
      },
      "noReason": {
        "title": "Job declined — reassign it",
        "body": "{{actor}} declined \"{{job}}\". Assign it to someone else to keep it moving."
      }
    },
    "job_message": {
      "photoNoSenderLegacy": {
        "title": "New message on job",
        "body": "Someone shared a photo"
      },
      "photoNoSender": {
        "title": "New message on job",
        "body": "Someone shared a photo."
      },
      "photoLegacy": {
        "title": "New message on job",
        "body": "{{sender}} shared a photo"
      },
      "photo": {
        "title": "New message on job",
        "body": "{{sender}} shared a photo."
      },
      "emptyUpdateNoSender": {
        "title": "New message on job",
        "body": "Someone: Update"
      },
      "noSender": {
        "title": "New message on job",
        "body": "Someone: {{message}}"
      },
      "emptyUpdate": {
        "title": "New message on job",
        "body": "{{sender}}: Update"
      },
      "default": {
        "title": "New message on job",
        "body": "{{sender}}: {{message}}"
      }
    },
    "job_reassigned": {
      "noJobLegacy": {
        "title": "Job reassigned",
        "body": "You are no longer assigned to A job."
      },
      "noJob": {
        "title": "Job reassigned",
        "body": "You are no longer assigned to a job."
      },
      "default": {
        "title": "Job reassigned",
        "body": "You are no longer assigned to {{job}}."
      }
    },
    "job_report_shared": {
      "recipientNoSharerNoJobNoAssetLegacy": {
        "title": "Job report ready",
        "body": "The team shared the completion report for \"a job\" on an asset."
      },
      "recipientNoSharerNoJobNoAsset": {
        "title": "Job report ready",
        "body": "The team shared the completion report for a job on an asset."
      },
      "recipientNoSharerNoAsset": {
        "title": "Job report ready",
        "body": "The team shared the completion report for \"{{job}}\" on an asset."
      },
      "recipientNoJobNoAssetLegacy": {
        "title": "Job report ready",
        "body": "{{sharer}} shared the completion report for \"a job\" on an asset."
      },
      "recipientNoJobNoAsset": {
        "title": "Job report ready",
        "body": "{{sharer}} shared the completion report for a job on an asset."
      },
      "recipientNoSharerNoJobLegacy": {
        "title": "Job report ready",
        "body": "The team shared the completion report for \"a job\" on {{asset}}."
      },
      "recipientNoSharerNoJob": {
        "title": "Job report ready",
        "body": "The team shared the completion report for a job on {{asset}}."
      },
      "recipientNoAsset": {
        "title": "Job report ready",
        "body": "{{sharer}} shared the completion report for \"{{job}}\" on an asset."
      },
      "recipientNoSharer": {
        "title": "Job report ready",
        "body": "The team shared the completion report for \"{{job}}\" on {{asset}}."
      },
      "recipientNoJobLegacy": {
        "title": "Job report ready",
        "body": "{{sharer}} shared the completion report for \"a job\" on {{asset}}."
      },
      "recipientNoJob": {
        "title": "Job report ready",
        "body": "{{sharer}} shared the completion report for a job on {{asset}}."
      },
      "sharerNoRecipient": {
        "title": "Job completion report shared",
        "body": "Report link sent to Recipient."
      },
      "recipient": {
        "title": "Job report ready",
        "body": "{{sharer}} shared the completion report for \"{{job}}\" on {{asset}}."
      },
      "resentNoRecipient": {
        "title": "Job completion report resent",
        "body": "Sent again to Recipient."
      },
      "sharer": {
        "title": "Job completion report shared",
        "body": "Report link sent to {{recipient}}."
      },
      "sharerBlankName": {
        "title": "Job completion report shared",
        "body": "Report link is ready."
      },
      "resentBlankName": {
        "title": "Job completion report resent",
        "body": "Report link updated."
      },
      "resent": {
        "title": "Job completion report resent",
        "body": "Sent again to {{recipient}}."
      }
    },
    "job_requester_accepted": {
      "verifierNoRequesterNoJob": {
        "title": "Requester is happy — verify to close",
        "body": "The requester accepted the completion of the job. Verify the job to close it."
      },
      "verifierNoRequester": {
        "title": "Requester is happy — verify to close",
        "body": "The requester accepted the completion of {{job}}. Verify the job to close it."
      },
      "verifierNoJob": {
        "title": "Requester is happy — verify to close",
        "body": "{{requester}} accepted the completion of the job. Verify the job to close it."
      },
      "verifier": {
        "title": "Requester is happy — verify to close",
        "body": "{{requester}} accepted the completion of {{job}}. Verify the job to close it."
      },
      "informedNoRequesterNoJob": {
        "title": "Requester accepted the work",
        "body": "The requester accepted the completion of the job."
      },
      "informedNoRequester": {
        "title": "Requester accepted the work",
        "body": "The requester accepted the completion of {{job}}."
      },
      "informedNoJob": {
        "title": "Requester accepted the work",
        "body": "{{requester}} accepted the completion of the job."
      },
      "informed": {
        "title": "Requester accepted the work",
        "body": "{{requester}} accepted the completion of {{job}}."
      }
    },
    "job_rework_requested": {
      "noRequesterNoJob": {
        "title": "Rework requested",
        "body": "The requester requested rework on the job: {{note}}"
      },
      "noRequesterNoJobNoNote": {
        "title": "Rework requested",
        "body": "The requester requested rework on the job."
      },
      "noRequester": {
        "title": "Rework requested",
        "body": "The requester requested rework on {{job}}: {{note}}"
      },
      "noRequesterNoNote": {
        "title": "Rework requested",
        "body": "The requester requested rework on {{job}}."
      },
      "noJob": {
        "title": "Rework requested",
        "body": "{{requester}} requested rework on the job: {{note}}"
      },
      "noJobNoNote": {
        "title": "Rework requested",
        "body": "{{requester}} requested rework on the job."
      },
      "default": {
        "title": "Rework requested",
        "body": "{{requester}} requested rework on {{job}}: {{note}}"
      },
      "noNote": {
        "title": "Rework requested",
        "body": "{{requester}} requested rework on {{job}}."
      }
    },
    "job_scheduled": {
      "noActorNoJobLegacy": {
        "title": "Visit planned",
        "body": "The assignee scheduled \"A job\" for {{when}}."
      },
      "noActorNoJob": {
        "title": "Visit planned",
        "body": "The assignee scheduled a job for {{when}}."
      },
      "noActor": {
        "title": "Visit planned",
        "body": "The assignee scheduled \"{{job}}\" for {{when}}."
      },
      "noJobLegacy": {
        "title": "Visit planned",
        "body": "{{actor}} scheduled \"A job\" for {{when}}."
      },
      "noJob": {
        "title": "Visit planned",
        "body": "{{actor}} scheduled a job for {{when}}."
      },
      "default": {
        "title": "Visit planned",
        "body": "{{actor}} scheduled \"{{job}}\" for {{when}}."
      }
    },
    "job_unassigned": {
      "noJobLegacy": {
        "title": "Job unassigned",
        "body": "You are no longer assigned to A job."
      },
      "noJob": {
        "title": "Job unassigned",
        "body": "You are no longer assigned to a job."
      },
      "default": {
        "title": "Job unassigned",
        "body": "You are no longer assigned to {{job}}."
      }
    },
    "job_verified": {
      "noActorNoJobLegacy": {
        "title": "Your work was verified",
        "body": "The owner verified \"a job\". The job is closed."
      },
      "noActorNoJob": {
        "title": "Your work was verified",
        "body": "The owner verified a job. The job is closed."
      },
      "noActor": {
        "title": "Your work was verified",
        "body": "The owner verified \"{{job}}\". The job is closed."
      },
      "noJobLegacy": {
        "title": "Your work was verified",
        "body": "{{actor}} verified \"a job\". The job is closed."
      },
      "noJob": {
        "title": "Your work was verified",
        "body": "{{actor}} verified a job. The job is closed."
      },
      "default": {
        "title": "Your work was verified",
        "body": "{{actor}} verified \"{{job}}\". The job is closed."
      }
    },
    "job_visit_proposed": {
      "noJob": {
        "title": "Confirm the visit time",
        "body": "A job is planned for {{when}} — confirm someone can be there, or ask for a different time."
      },
      "default": {
        "title": "Confirm the visit time",
        "body": "{{job}} is planned for {{when}} — confirm someone can be there, or ask for a different time."
      }
    },
    "mcp_action_proposed": {
      "oneNoClient": {
        "title": "AI requests to approve",
        "body": "An AI assistant asks to: {{action}}. Approve or decline it before it expires."
      },
      "many": {
        "title": "AI requests to approve",
        "body": "{{total}} changes from AI assistants are waiting for your approval."
      },
      "one": {
        "title": "AI requests to approve",
        "body": "{{client}} asks to: {{action}}. Approve or decline it before it expires."
      }
    },
    "member_removed_reassign": {
      "noLeaverNoJobLegacy": {
        "title": "Reassign work left behind",
        "body": "Someone left the team with \"a job\" still assigned to them. Assign it to someone else."
      },
      "noLeaverNoJob": {
        "title": "Reassign work left behind",
        "body": "Someone left the team with a job still assigned to them. Assign it to someone else."
      },
      "noLeaver": {
        "title": "Reassign work left behind",
        "body": "Someone left the team with \"{{job}}\" still assigned to them. Assign it to someone else."
      },
      "noJobLegacy": {
        "title": "Reassign work left behind",
        "body": "{{leaver}} left the team with \"a job\" still assigned to them. Assign it to someone else."
      },
      "noJob": {
        "title": "Reassign work left behind",
        "body": "{{leaver}} left the team with a job still assigned to them. Assign it to someone else."
      },
      "default": {
        "title": "Reassign work left behind",
        "body": "{{leaver}} left the team with \"{{job}}\" still assigned to them. Assign it to someone else."
      }
    },
    "mfa_reset_requested": {
      "noMember": {
        "title": "Two-factor reset requested",
        "body": "A workspace member is locked out of two-factor sign-in and asked for a reset."
      },
      "default": {
        "title": "Two-factor reset requested",
        "body": "{{member}} is locked out of two-factor sign-in and asked for a reset."
      }
    },
    "plan_changed": {
      "byActorLegacy": {
        "title": "Your plan for {{day}} changed",
        "body": "{{actor}} changed {{total}} items in your day. Open My Day to see it."
      },
      "byActor": {
        "title": "Your plan for {{day}} changed",
        "body": "{{actor}} changed {{total}} items in your day. Open My Day to see them."
      },
      "defaultLegacy": {
        "title": "Your plan for {{day}} changed",
        "body": "Changed: {{total}} items in your day. Open My Day to see it."
      },
      "default": {
        "title": "Your plan for {{day}} changed",
        "body": "Changed: {{total}} items in your day. Open My Day to see them."
      },
      "byActorOne": {
        "title": "Your plan for {{day}} changed",
        "body": "{{actor}} changed {{total}} item in your day. Open My Day to see it."
      },
      "one": {
        "title": "Your plan for {{day}} changed",
        "body": "Changed: {{total}} item in your day. Open My Day to see it."
      }
    },
    "plan_review": {
      "default": {
        "title": "The plan for {{day}} needs a look",
        "body": "Something in the day no longer holds. Open the calendar to review the suggested plan."
      }
    },
    "quote_declined": {
      "noContractor": {
        "title": "Quote request declined",
        "body": "A contractor declined to quote on {{job}}."
      },
      "noContractorNoJob": {
        "title": "Quote request declined",
        "body": "A contractor declined to quote."
      },
      "default": {
        "title": "Quote request declined",
        "body": "{{contractor}} declined to quote on {{job}}."
      },
      "noJob": {
        "title": "Quote request declined",
        "body": "{{contractor}} declined to quote."
      }
    },
    "quote_rejected": {
      "noJobLegacy": {
        "title": "Quote not selected",
        "body": "Your quote for \"a job\" was reviewed but not selected."
      },
      "noJob": {
        "title": "Quote not selected",
        "body": "Your quote for a job was reviewed but not selected."
      },
      "default": {
        "title": "Quote not selected",
        "body": "Your quote for \"{{job}}\" was reviewed but not selected."
      }
    },
    "quote_requested": {
      "noJobLegacy": {
        "title": "Quote request",
        "body": "You have been invited to submit a quote for: A job."
      },
      "noJob": {
        "title": "Quote request",
        "body": "You have been invited to submit a quote for: a job."
      },
      "default": {
        "title": "Quote request",
        "body": "You have been invited to submit a quote for: {{job}}."
      }
    },
    "quote_submitted": {
      "taxNoContractorNoJob": {
        "title": "New quote received",
        "body": "A contractor quoted {{amount}} incl. tax for a job. Review the quotes."
      },
      "taxNoContractor": {
        "title": "New quote received",
        "body": "A contractor quoted {{amount}} incl. tax for \"{{job}}\". Review the quotes."
      },
      "taxNoJob": {
        "title": "New quote received",
        "body": "{{contractor}} quoted {{amount}} incl. tax for a job. Review the quotes."
      },
      "tax": {
        "title": "New quote received",
        "body": "{{contractor}} quoted {{amount}} incl. tax for \"{{job}}\". Review the quotes."
      },
      "noContractorNoPriceNoJobLegacy": {
        "title": "New quote received",
        "body": "A contractor submitted a quote for \"a job\". Review the quotes."
      },
      "noContractorNoPriceNoJob": {
        "title": "New quote received",
        "body": "A contractor submitted a quote for a job. Review the quotes."
      },
      "noContractorNoPrice": {
        "title": "New quote received",
        "body": "A contractor submitted a quote for \"{{job}}\". Review the quotes."
      },
      "noContractorNoJobLegacy": {
        "title": "New quote received",
        "body": "A contractor quoted {{amount}} for \"a job\". Review the quotes."
      },
      "noContractorNoJob": {
        "title": "New quote received",
        "body": "A contractor quoted {{amount}} for a job. Review the quotes."
      },
      "noPriceNoJobLegacy": {
        "title": "New quote received",
        "body": "{{contractor}} submitted a quote for \"a job\". Review the quotes."
      },
      "noPriceNoJob": {
        "title": "New quote received",
        "body": "{{contractor}} submitted a quote for a job. Review the quotes."
      },
      "noContractor": {
        "title": "New quote received",
        "body": "A contractor quoted {{amount}} for \"{{job}}\". Review the quotes."
      },
      "noPrice": {
        "title": "New quote received",
        "body": "{{contractor}} submitted a quote for \"{{job}}\". Review the quotes."
      },
      "noJobLegacy": {
        "title": "New quote received",
        "body": "{{contractor}} quoted {{amount}} for \"a job\". Review the quotes."
      },
      "noJob": {
        "title": "New quote received",
        "body": "{{contractor}} quoted {{amount}} for a job. Review the quotes."
      },
      "default": {
        "title": "New quote received",
        "body": "{{contractor}} quoted {{amount}} for \"{{job}}\". Review the quotes."
      }
    },
    "report_access_declined": {
      "noAsset": {
        "title": "Report access declined",
        "body": "Your request to view the report for the asset was declined."
      },
      "default": {
        "title": "Report access declined",
        "body": "Your request to view the report for {{asset}} was declined."
      }
    },
    "report_access_granted": {
      "noAsset": {
        "title": "Report access approved",
        "body": "You can now view the report for the asset."
      },
      "default": {
        "title": "Report access approved",
        "body": "You can now view the report for {{asset}}."
      }
    },
    "report_access_requested": {
      "noRequesterNoAsset": {
        "title": "Report access requested",
        "body": "Someone asked to view the report for an asset."
      },
      "noAsset": {
        "title": "Report access requested",
        "body": "{{requester}} asked to view the report for an asset."
      },
      "noRequester": {
        "title": "Report access requested",
        "body": "Someone asked to view the report for {{asset}}."
      },
      "default": {
        "title": "Report access requested",
        "body": "{{requester}} asked to view the report for {{asset}}."
      },
      "noRequesterNoAssetLegacy": {
        "title": "Report access requested",
        "body": "Someone asked to view the report for an asset"
      },
      "noAssetLegacy": {
        "title": "Report access requested",
        "body": "{{requester}} asked to view the report for an asset"
      },
      "noRequesterLegacy": {
        "title": "Report access requested",
        "body": "Someone asked to view the report for {{asset}}"
      },
      "defaultLegacy": {
        "title": "Report access requested",
        "body": "{{requester}} asked to view the report for {{asset}}"
      }
    },
    "request_assigned": {
      "default": {
        "title": "Request assigned to you",
        "body": "{{request}} is waiting for your action."
      },
      "byActor": {
        "title": "Request assigned to you",
        "body": "{{actor}} assigned you \"{{request}}\"."
      }
    },
    "request_reassigned": {
      "default": {
        "title": "Request reassigned",
        "body": "You are no longer assigned to {{request}}."
      }
    },
    "request_team_assigned": {
      "noTeam": {
        "title": "Request moved to your team",
        "body": "{{request}} is now managed by your team."
      },
      "default": {
        "title": "Request moved to your team",
        "body": "{{request}} is now managed by {{team}}."
      }
    },
    "request_team_removed": {
      "default": {
        "title": "Request moved from your team",
        "body": "{{request}} is no longer managed by your team."
      }
    },
    "rework_requested": {
      "noActorNoJobLegacy": {
        "title": "Rework needed on your job",
        "body": "The reviewer sent \"a job\" back for rework. Reason: {{reason}}."
      },
      "noActorNoJob": {
        "title": "Rework needed on your job",
        "body": "The reviewer sent a job back for rework. Reason: {{reason}}"
      },
      "noActor": {
        "title": "Rework needed on your job",
        "body": "The reviewer sent \"{{job}}\" back for rework. Reason: {{reason}}"
      },
      "noActorNoJobNoReasonLegacy": {
        "title": "Rework needed on your job",
        "body": "The reviewer sent \"a job\" back for rework."
      },
      "noActorNoJobNoReason": {
        "title": "Rework needed on your job",
        "body": "The reviewer sent a job back for rework."
      },
      "noJobLegacy": {
        "title": "Rework needed on your job",
        "body": "{{actor}} sent \"a job\" back for rework. Reason: {{reason}}."
      },
      "noJob": {
        "title": "Rework needed on your job",
        "body": "{{actor}} sent a job back for rework. Reason: {{reason}}"
      },
      "noActorNoReason": {
        "title": "Rework needed on your job",
        "body": "The reviewer sent \"{{job}}\" back for rework."
      },
      "default": {
        "title": "Rework needed on your job",
        "body": "{{actor}} sent \"{{job}}\" back for rework. Reason: {{reason}}"
      },
      "noJobNoReasonLegacy": {
        "title": "Rework needed on your job",
        "body": "{{actor}} sent \"a job\" back for rework."
      },
      "noJobNoReason": {
        "title": "Rework needed on your job",
        "body": "{{actor}} sent a job back for rework."
      },
      "noReason": {
        "title": "Rework needed on your job",
        "body": "{{actor}} sent \"{{job}}\" back for rework."
      }
    },
    "share_message": {
      "noSender": {
        "title": "New message on inspection report",
        "body": "Someone: {{message}}"
      },
      "default": {
        "title": "New message on inspection report",
        "body": "{{sender}}: {{message}}"
      }
    },
    "share_response_submitted": {
      "closedOnBehalfNoActorNoRecipient": {
        "title": "Report closed without a response",
        "body": "The asset team closed the report for {{asset}} on the recipient's behalf: {{reason}}"
      },
      "closedOnBehalfNoActorNoRecipientNoReason": {
        "title": "Report closed without a response",
        "body": "The asset team closed the report for {{asset}} on the recipient's behalf."
      },
      "approvedOnBehalfNoActorNoRecipient": {
        "title": "Inspection report approved",
        "body": "The asset team signed off the report for {{asset}} on the recipient's behalf."
      },
      "closedOnBehalfNoActorNoAssetNoRecipient": {
        "title": "Report closed without a response",
        "body": "The asset team closed the report on the recipient's behalf: {{reason}}"
      },
      "closedOnBehalfNoActorNoAssetNoRecipientNoReason": {
        "title": "Report closed without a response",
        "body": "The asset team closed the report on the recipient's behalf."
      },
      "approvedOnBehalfNoActorNoAssetNoRecipient": {
        "title": "Inspection report approved",
        "body": "The asset team signed off the report on the recipient's behalf."
      },
      "closedOnBehalfNoActor": {
        "title": "Report closed without a response",
        "body": "The asset team closed the report for {{asset}} on {{recipient}}'s behalf: {{reason}}"
      },
      "closedOnBehalfNoRecipient": {
        "title": "Report closed without a response",
        "body": "{{actor}} closed the report for {{asset}} on the recipient's behalf: {{reason}}"
      },
      "closedOnBehalfNoActorNoReason": {
        "title": "Report closed without a response",
        "body": "The asset team closed the report for {{asset}} on {{recipient}}'s behalf."
      },
      "closedOnBehalfNoRecipientNoReason": {
        "title": "Report closed without a response",
        "body": "{{actor}} closed the report for {{asset}} on the recipient's behalf."
      },
      "approvedOnBehalfNoActor": {
        "title": "Inspection report approved",
        "body": "The asset team signed off the report for {{asset}} on {{recipient}}'s behalf."
      },
      "changesNoSigner": {
        "title": "Changes requested on a report",
        "body": "The recipient requested changes to the report for {{asset}}: {{reason}}"
      },
      "approvedOnBehalfNoRecipient": {
        "title": "Inspection report approved",
        "body": "{{actor}} signed off the report for {{asset}} on the recipient's behalf."
      },
      "changesNoSignerNoReason": {
        "title": "Changes requested on a report",
        "body": "The recipient requested changes to the report for {{asset}}."
      },
      "closedOnBehalfNoActorNoAsset": {
        "title": "Report closed without a response",
        "body": "The asset team closed the report on {{recipient}}'s behalf: {{reason}}"
      },
      "closedOnBehalfNoAssetNoRecipient": {
        "title": "Report closed without a response",
        "body": "{{actor}} closed the report on the recipient's behalf: {{reason}}"
      },
      "closedOnBehalfNoActorNoAssetNoReason": {
        "title": "Report closed without a response",
        "body": "The asset team closed the report on {{recipient}}'s behalf."
      },
      "closedOnBehalfNoAssetNoRecipientNoReason": {
        "title": "Report closed without a response",
        "body": "{{actor}} closed the report on the recipient's behalf."
      },
      "approvedOnBehalfNoActorNoAsset": {
        "title": "Inspection report approved",
        "body": "The asset team signed off the report on {{recipient}}'s behalf."
      },
      "changesNoSignerNoAsset": {
        "title": "Changes requested on a report",
        "body": "The recipient requested changes to the report: {{reason}}"
      },
      "approvedOnBehalfNoAssetNoRecipient": {
        "title": "Inspection report approved",
        "body": "{{actor}} signed off the report on the recipient's behalf."
      },
      "changesNoSignerNoAssetNoReason": {
        "title": "Changes requested on a report",
        "body": "The recipient requested changes to the report."
      },
      "closedOnBehalf": {
        "title": "Report closed without a response",
        "body": "{{actor}} closed the report for {{asset}} on {{recipient}}'s behalf: {{reason}}"
      },
      "closedOnBehalfNoReason": {
        "title": "Report closed without a response",
        "body": "{{actor}} closed the report for {{asset}} on {{recipient}}'s behalf."
      },
      "changes": {
        "title": "Changes requested on a report",
        "body": "{{signer}} requested changes to the report for {{asset}}: {{reason}}"
      },
      "approvedNoSigner": {
        "title": "Inspection report approved",
        "body": "The recipient signed off the report for {{asset}}."
      },
      "approvedOnBehalf": {
        "title": "Inspection report approved",
        "body": "{{actor}} signed off the report for {{asset}} on {{recipient}}'s behalf."
      },
      "changesNoReason": {
        "title": "Changes requested on a report",
        "body": "{{signer}} requested changes to the report for {{asset}}."
      },
      "closedOnBehalfNoAsset": {
        "title": "Report closed without a response",
        "body": "{{actor}} closed the report on {{recipient}}'s behalf: {{reason}}"
      },
      "closedOnBehalfNoAssetNoReason": {
        "title": "Report closed without a response",
        "body": "{{actor}} closed the report on {{recipient}}'s behalf."
      },
      "changesNoAsset": {
        "title": "Changes requested on a report",
        "body": "{{signer}} requested changes to the report: {{reason}}"
      },
      "approvedNoSignerNoAsset": {
        "title": "Inspection report approved",
        "body": "The recipient signed off the report."
      },
      "approvedOnBehalfNoAsset": {
        "title": "Inspection report approved",
        "body": "{{actor}} signed off the report on {{recipient}}'s behalf."
      },
      "changesNoAssetNoReason": {
        "title": "Changes requested on a report",
        "body": "{{signer}} requested changes to the report."
      },
      "approved": {
        "title": "Inspection report approved",
        "body": "{{signer}} signed off the report for {{asset}}."
      },
      "approvedNoAsset": {
        "title": "Inspection report approved",
        "body": "{{signer}} signed off the report."
      }
    },
    "site_team_assigned": {
      "noTeam": {
        "title": "New site in your team",
        "body": "{{site}} has been assigned to your team."
      },
      "default": {
        "title": "New site in your team",
        "body": "{{site}} has been assigned to {{team}}."
      }
    },
    "site_team_removed": {
      "default": {
        "title": "Site moved from your team",
        "body": "{{site}} is no longer assigned to your team."
      }
    },
    "stalled_work_digest": {
      "many": {
        "title": "{{total}} things are waiting on someone",
        "body": "{{itemTitle}} · {{asset}}. {{line}} And {{moreCount}} more."
      },
      "manyNoAsset": {
        "title": "{{total}} things are waiting on someone",
        "body": "{{itemTitle}}. {{line}} And {{moreCount}} more."
      },
      "one": {
        "title": "Something is waiting on someone",
        "body": "{{itemTitle}} · {{asset}}. {{line}}"
      },
      "oneNoAsset": {
        "title": "Something is waiting on someone",
        "body": "{{itemTitle}}. {{line}}"
      }
    },
    "team_admin_approved": {
      "noMemberLegacy": {
        "title": "A team member is now an admin",
        "body": "The owner approved your request for A team member."
      },
      "noMember": {
        "title": "A team member is now an admin",
        "body": "The owner approved your request for a team member."
      },
      "default": {
        "title": "{{member}} is now an admin",
        "body": "The owner approved your request for {{member}}."
      }
    },
    "team_admin_declined": {
      "noMemberLegacy": {
        "title": "Admin request declined",
        "body": "The owner declined your request for A team member to become an admin."
      },
      "noMember": {
        "title": "Admin request declined",
        "body": "The owner declined your request for a team member to become an admin."
      },
      "default": {
        "title": "Admin request declined",
        "body": "The owner declined your request for {{member}} to become an admin."
      }
    },
    "team_admin_requested": {
      "noMemberNoTeamLegacy": {
        "title": "Make A team member an admin?",
        "body": "Your team: an admin asks for A team member to become an admin."
      },
      "noMemberNoTeam": {
        "title": "Make a team member an admin?",
        "body": "Your team: an admin asks for a team member to become an admin."
      },
      "noMemberLegacy": {
        "title": "Make A team member an admin?",
        "body": "{{team}}: an admin asks for A team member to become an admin."
      },
      "noMember": {
        "title": "Make a team member an admin?",
        "body": "{{team}}: an admin asks for a team member to become an admin."
      },
      "noTeam": {
        "title": "Make {{member}} an admin?",
        "body": "Your team: an admin asks for {{member}} to become an admin."
      },
      "default": {
        "title": "Make {{member}} an admin?",
        "body": "{{team}}: an admin asks for {{member}} to become an admin."
      }
    },
    "team_invite_accepted": {
      "noTeam": {
        "title": "Workspace invitation accepted",
        "body": "{{email}} accepted the invitation to your team."
      },
      "default": {
        "title": "Workspace invitation accepted",
        "body": "{{email}} accepted the invitation to {{team}}."
      }
    },
    "team_invite_declined": {
      "noTeam": {
        "title": "Workspace invitation declined",
        "body": "{{email}} declined the invitation to your team."
      },
      "default": {
        "title": "Workspace invitation declined",
        "body": "{{email}} declined the invitation to {{team}}."
      }
    },
    "team_invited": {
      "noTeam": {
        "title": "Workspace invitation",
        "body": "You were invited to join a workspace team."
      },
      "default": {
        "title": "Workspace invitation",
        "body": "You were invited to join {{team}}."
      }
    },
    "team_member_added": {
      "noTeam": {
        "title": "Added to workspace team",
        "body": "Your team."
      },
      "default": {
        "title": "Added to workspace team",
        "body": "{{team}}."
      }
    },
    "team_member_removed": {
      "noTeam": {
        "title": "Removed from workspace team",
        "body": "Your team."
      },
      "default": {
        "title": "Removed from workspace team",
        "body": "{{team}}."
      }
    },
    "team_member_role_changed": {
      "noTeam": {
        "title": "Workspace role changed",
        "body": "Your team · role {{role}}"
      },
      "default": {
        "title": "Workspace role changed",
        "body": "{{team}} · role {{role}}"
      }
    },
    "team_ownership_accepted": {
      "noWorkspace": {
        "title": "Workspace ownership accepted",
        "body": "The workspace ownership transfer was accepted."
      },
      "default": {
        "title": "Workspace ownership accepted",
        "body": "{{workspace}} ownership transfer was accepted."
      }
    },
    "team_ownership_cancelled": {
      "noWorkspace": {
        "title": "Workspace ownership cancelled",
        "body": "The workspace ownership transfer was cancelled."
      },
      "default": {
        "title": "Workspace ownership cancelled",
        "body": "{{workspace}} ownership transfer was cancelled."
      }
    },
    "team_ownership_declined": {
      "noWorkspace": {
        "title": "Workspace ownership declined",
        "body": "The workspace ownership transfer was declined."
      },
      "default": {
        "title": "Workspace ownership declined",
        "body": "{{workspace}} ownership transfer was declined."
      }
    },
    "team_ownership_expired": {
      "noWorkspaceLegacy": {
        "title": "Workspace ownership expired",
        "body": "The workspace ownership transfer was expired."
      },
      "noWorkspace": {
        "title": "Workspace ownership expired",
        "body": "The workspace ownership transfer has expired."
      },
      "defaultLegacy": {
        "title": "Workspace ownership expired",
        "body": "{{workspace}} ownership transfer was expired."
      },
      "default": {
        "title": "Workspace ownership expired",
        "body": "{{workspace}} ownership transfer has expired."
      }
    },
    "team_ownership_requested": {
      "noWorkspace": {
        "title": "Workspace ownership offered",
        "body": "A workspace is waiting for your decision."
      },
      "default": {
        "title": "Workspace ownership offered",
        "body": "{{workspace}} is waiting for your decision."
      }
    },
    "work_change_approved": {
      "byPolicyNoWork": {
        "title": "Skipped under plan policy: scheduled work",
        "body": "{{reason}}"
      },
      "byPolicy": {
        "title": "Skipped under plan policy: {{work}}",
        "body": "{{reason}}"
      },
      "decided": {
        "title": "Change approved",
        "body": "{{action}} approved"
      }
    },
    "work_change_rejected": {
      "default": {
        "title": "Change not approved",
        "body": "This work is still due"
      }
    },
    "work_change_requested": {
      "noWork": {
        "title": "Change requested: scheduled work",
        "body": "{{reason}}"
      },
      "default": {
        "title": "Change requested: {{work}}",
        "body": "{{reason}}"
      }
    },
    "work_needs_assignee": {
      "raisedNoAsset": {
        "title": "Nobody is assigned yet",
        "body": "\"{{work}}\" is due {{dueDate}}. Assign someone before then."
      },
      "reminderNoAsset": {
        "title": "Still nobody assigned",
        "body": "\"{{work}}\" is due {{dueDate}}. Assign someone or do it yourself."
      },
      "raised": {
        "title": "Nobody is assigned yet",
        "body": "\"{{work}}\" · {{asset}} is due {{dueDate}}. Assign someone before then."
      },
      "reminder": {
        "title": "Still nobody assigned",
        "body": "\"{{work}}\" · {{asset}} is due {{dueDate}}. Assign someone or do it yourself."
      }
    },
    "work_rescheduled": {
      "noInspection": {
        "title": "Rescheduled",
        "body": "Inspection · {{asset}} is now {{when}}."
      },
      "noInspectionNoAsset": {
        "title": "Rescheduled",
        "body": "Inspection is now {{when}}."
      },
      "noJob": {
        "title": "Rescheduled",
        "body": "A job · {{asset}} is now {{when}}."
      },
      "noJobNoAsset": {
        "title": "Rescheduled",
        "body": "A job is now {{when}}."
      },
      "default": {
        "title": "Rescheduled",
        "body": "{{work}} · {{asset}} is now {{when}}."
      },
      "noAsset": {
        "title": "Rescheduled",
        "body": "{{work}} is now {{when}}."
      }
    },
    "work_unscheduled": {
      "noInspection": {
        "title": "No longer scheduled",
        "body": "Inspection · {{asset}} is no longer scheduled."
      },
      "noInspectionNoAsset": {
        "title": "No longer scheduled",
        "body": "Inspection is no longer scheduled."
      },
      "noJob": {
        "title": "No longer scheduled",
        "body": "A job · {{asset}} is no longer scheduled."
      },
      "noJobNoAsset": {
        "title": "No longer scheduled",
        "body": "A job is no longer scheduled."
      },
      "default": {
        "title": "No longer scheduled",
        "body": "{{work}} · {{asset}} is no longer scheduled."
      },
      "noAsset": {
        "title": "No longer scheduled",
        "body": "{{work}} is no longer scheduled."
      }
    },
    "esg_certificate_expiring": {
      "expired": {
        "title": "Energy certificate expired: {{place}}",
        "body": "The {{scheme}} (rating {{rating}}) runs out on {{endDate}}. Book a new assessment so it can still be let or sold."
      },
      "inDays": {
        "title": "Energy certificate expires in {{daysLeft}} days: {{place}}",
        "body": "The {{scheme}} (rating {{rating}}) runs out on {{endDate}}. Book a new assessment so it can still be let or sold."
      }
    }
  },
  "lines": {
    "shareNotAcceptedOne": "{{waitingOn}} has not accepted access to this asset for {{days}} day. Chase them or remove the share.",
    "shareNotAccepted": "{{waitingOn}} has not accepted access to this asset for {{days}} days. Chase them or remove the share.",
    "expiredNotRenewedOne": "Expired {{days}} day ago and nobody has renewed it.",
    "expiredNotRenewed": "Expired {{days}} days ago and nobody has renewed it.",
    "fixNotConfirmedOne": "{{waitingOn}} has not confirmed the fix for {{days}} day. Chase them.",
    "fixNotConfirmed": "{{waitingOn}} has not confirmed the fix for {{days}} days. Chase them.",
    "notRedoneOne": "{{waitingOn}} has not redone this for {{days}} day. Chase them or reassign it.",
    "notRedone": "{{waitingOn}} has not redone this for {{days}} days. Chase them or reassign it.",
    "inviteNotAcceptedOne": "{{waitingOn}} has not accepted the invitation for {{days}} day. Resend or withdraw it.",
    "inviteNotAccepted": "{{waitingOn}} has not accepted the invitation for {{days}} days. Resend or withdraw it.",
    "approvedNoWorkOne": "Approved {{days}} day ago and no work has been created yet.",
    "approvedNoWork": "Approved {{days}} days ago and no work has been created yet.",
    "overdueOne": "Due {{days}} day ago and not done yet.",
    "overdue": "Due {{days}} days ago and not done yet.",
    "overdueUnassignedOne": "Due {{days}} day ago and nobody is assigned to it. Assign it or do it yourself.",
    "overdueUnassigned": "Due {{days}} days ago and nobody is assigned to it. Assign it or do it yourself.",
    "overdueNotDoneOne": "Due {{days}} day ago and {{waitingOn}} has not done it. Reassign it or do it yourself.",
    "overdueNotDone": "Due {{days}} days ago and {{waitingOn}} has not done it. Reassign it or do it yourself.",
    "quotesStalledOne": "Nothing has moved on these quotes for {{days}} day. Award one, chase, or set a deadline.",
    "quotesStalled": "Nothing has moved on these quotes for {{days}} days. Award one, chase, or set a deadline.",
    "awaitingYourValidationOne": "Waiting on your validation for {{days}} day.",
    "awaitingYourValidation": "Waiting on your validation for {{days}} days.",
    "quoteDeadlinePassedOne": "The quote deadline passed {{days}} day ago. Award one, chase, or set a new date.",
    "quoteDeadlinePassed": "The quote deadline passed {{days}} days ago. Award one, chase, or set a new date.",
    "awaitingYourPickupOne": "Waiting on you to pick this up for {{days}} day.",
    "awaitingYourPickup": "Waiting on you to pick this up for {{days}} days.",
    "awaitingYourApprovalOne": "Waiting on your approval for {{days}} day.",
    "awaitingYourApproval": "Waiting on your approval for {{days}} days.",
    "noResponseOne": "No response from {{waitingOn}} for {{days}} day.",
    "noResponse": "No response from {{waitingOn}} for {{days}} days.",
    "notValidatedOne": "{{waitingOn}} has not validated this for {{days}} day.",
    "notValidated": "{{waitingOn}} has not validated this for {{days}} days.",
    "inviteNotAnsweredOne": "{{waitingOn}} has not answered the invitation for {{days}} day. Chase them or assign someone else.",
    "inviteNotAnswered": "{{waitingOn}} has not answered the invitation for {{days}} days. Chase them or assign someone else.",
    "notRepliedOne": "{{waitingOn}} has not replied for {{days}} day.",
    "notReplied": "{{waitingOn}} has not replied for {{days}} days.",
    "notApprovedOne": "{{waitingOn}} has not approved this for {{days}} day.",
    "notApproved": "{{waitingOn}} has not approved this for {{days}} days."
  },
  "values": {
    "featureStatus": {
      "under_review": "under review",
      "planned": "planned",
      "in_progress": "being built",
      "shipped": "in the app now",
      "not_planned": "not planned for now",
      "considering": "being considered"
    },
    "reason": {
      "fault": "fault",
      "end_of_life": "end of life",
      "replaced": "replaced",
      "seasonal": "seasonal",
      "relocated": "relocated",
      "decommissioned": "decommissioned",
      "sold": "sold",
      "disposed": "disposed",
      "lost": "lost",
      "other": "other"
    },
    "period": {
      "am": "AM",
      "pm": "PM"
    },
    "periodLabel": {
      "morning": "morning",
      "afternoon": "afternoon"
    },
    "documentType": {
      "certificate": "certificate",
      "warranty": "warranty",
      "compliance_document": "compliance document",
      "document": "document"
    },
    "role": {
      "owner": "owner",
      "admin": "admin",
      "member": "member"
    },
    "action": {
      "skip": "Skip",
      "reschedule": "Reschedule",
      "reassign": "Reassign"
    },
    "itemTitle": {
      "inspection": "Inspection",
      "job": "Job",
      "document": "Document",
      "request": "Request",
      "scheduled_inspection": "Scheduled inspection",
      "invitation_to_the_team": "Invitation to the team"
    },
    "waitingOn": {
      "the_recipient": "the recipient",
      "asset_approver": "Asset approver",
      "the_approver": "the approver",
      "the_requester": "the requester",
      "the_assignee": "the assignee",
      "the_validator": "the validator",
      "the_team": "the team",
      "the_contractors_you_asked": "the contractors you asked",
      "the_contractor": "the contractor",
      "the_invitee": "the invitee",
      "nobody": "nobody",
      "The_recipient": "The recipient",
      "The_approver": "The approver",
      "The_requester": "The requester",
      "The_assignee": "The assignee",
      "The_validator": "The validator",
      "The_team": "The team",
      "The_contractor": "The contractor",
      "The_invitee": "The invitee"
    }
  }
};
